/**
 * One layout, many languages.
 *
 * Copy is resolved at render time from these dictionaries; no screen is authored
 * twice. Spanish and English ship together from the first screen, because
 * retrofitting i18n is a refactor and doing it now is a habit — see spec.md,
 * "Multilingual from the first commit".
 *
 * The dictionaries are typed against the English one, so a missing Spanish key
 * is a compile error rather than an English word appearing mid-sentence in a
 * Spanish paragraph.
 */
import { createContext, useContext } from 'react';
import type { Locale, Localised } from '@nabu/shared';

export const LOCALES: readonly Locale[] = ['es', 'en'];

/** Spanish is the default; English ships alongside it. */
export const DEFAULT_LOCALE: Locale = 'es';

const en = {
  'lang.name': 'English',
  'lang.switch': 'Change language',

  'nav.home': 'Home',

  'home.tagline': 'Learn it by being asked.',
  'home.lede':
    'Tell nabu what you want to memorise, add the books and pages you already study from, and say when you need to know it. It builds a plan and then teaches you in the app — asking more about what you keep getting wrong, and less about what you have.',

  'home.how.title': 'How it works',
  'home.how.1.title': 'Say what you want to learn',
  'home.how.1.body':
    'In your own words. Add PDFs, photographs of book pages, or links to anything you already study from.',
  'home.how.2.title': 'Review the plan',
  'home.how.2.body':
    'nabu researches the subject, reads your material and proposes a curriculum. You reorder it, weight it, and ask for more where it looks thin.',
  'home.how.3.title': 'Answer in the app',
  'home.how.3.body':
    'Questions arrive when you are reachable and at the moment you are about to forget. Wrong answers come back sooner; right ones come back later.',

  'home.why.title': 'Why it asks when it does',
  'home.why.body':
    'Every answer updates a model of what you remember, and the schedule follows from it — not from a fixed calendar. Say you have an exam on a date and the intervals compress toward it. Say you want to hold 85% and it paces itself to that instead.',

  'home.cta.title': 'Being built',
  'home.cta.body':
    'Signing in works. Stating an objective and getting a curriculum are next — so there is nothing to do inside yet, but you can get an account.',
  // Names neither channel, which was right when there were two and is still right with
  // one: a button that says "sign in" does not have to be rewritten the next time the way
  // in changes.
  'home.cta.button': 'Sign in',

  'signin.title': 'Sign in',
  'signin.code.label': 'The six-digit code',
  'signin.code.hint': 'It expires in ten minutes.',
  'signin.code.submit': 'Sign in',
  'signin.working': 'One moment…',
  /*
   * `signin.done.title` and `signin.done.body` are gone with F-138.
   *
   * They were the screen that said "Reload the page and you should still be signed in" —
   * a note from whoever built it, left where a learner reads it, at the one moment the
   * product had just earned some trust. Signing in now ends on whatever screen they were
   * going to, so there is nothing for that panel to say and it is deleted rather than
   * rewritten: a copy key nobody renders is a screen somebody rebuilds.
   */
  'signin.error.code': 'That code is wrong or has expired. Ask for another.',
  /*
   * The code was right and the session did not come back. Two things do that — a browser
   * refusing our cookie, and a network that dropped between the two requests, which
   * `whoAmI` reports as "not signed in" because that is the safe answer for a landing page.
   * The copy names both rather than blaming the cookie at somebody whose train went into a
   * tunnel. Its own sentence rather than the generic failure, because "try again" alone
   * leaves somebody retyping a code that is already spent.
   */
  'signin.error.session':
    'Your code was right, but the session did not come back — you may be offline, or this browser may be blocking cookies for nabu.guru. Check both and sign in again.',
  'signin.error.tooMany': 'Too many codes requested. Wait a little and try again.',
  'signin.error.generic': 'Something went wrong. Try again.',

  /*
   * Signing in by email (F-101), and since F-103 the only way in.
   *
   * The channel question and the number field went with the sidecar: `signin.channel.*`,
   * `signin.number.*`, `signin.code.back` and `signin.error.number` are deleted rather than
   * left unused, because a copy key nobody renders is a screen somebody will rebuild.
   */
  'signin.email.label': 'Your email address',
  /*
   * It said "nothing else is ever sent there", which F-122 made false on the day it
   * shipped: a learner with no notifications now gets a nudge at the same address. A
   * sign-in screen that promises less mail than the system sends is the promise somebody
   * quotes back at us, so it names the one other thing and says it can be stopped.
   */
  'signin.email.hint':
    'The code arrives by email. The only other thing we ever send there is a nudge when a ' +
    'question is waiting and notifications cannot reach you, and you can stop that whenever ' +
    'you like.',
  'signin.email.back': 'Change address',
  'signin.error.email': 'That does not look like an email address.',
  // It used to say "use WhatsApp for now", which after F-103 would send somebody looking
  // for a field that no longer exists. There is nothing for them to do instead, so the copy
  // says whose problem it is.
  'signin.error.unconfigured':
    'Sign-in codes cannot be sent right now. This is ours to fix rather than yours — try again shortly.',

  'email.title': 'Signing in by email',
  'email.body':
    'Add an email address and it becomes a second way into this same account — the same objectives, the same curriculum, the same history. Nothing is sent to it except a sign-in code.',
  'email.label': 'Email address',
  'email.submit': 'Send me a code',
  'email.code.label': 'The six-digit code',
  'email.code.hint': 'It expires in ten minutes.',
  'email.confirm': 'Add this address',
  'email.cancel': 'Cancel',
  'email.done': 'Done. You can now sign in with {email}.',
  'email.error.email': 'That does not look like an email address.',
  'email.error.code': 'That code is wrong or has expired. Ask for another.',
  'email.error.inUse': 'That address already signs in to another account.',
  'email.error.tooMany': 'Too many codes requested. Wait a little and try again.',
  'email.error.generic': 'Something went wrong. Try again.',

  // ---- Mail: whether it arrives, and the part you can stop (F-106) ----
  'mail.title': 'Email to this address',
  'mail.body':
    'Sign-in codes always come by email. So does a nudge when a question is waiting and ' +
    'notifications cannot reach you. Each of the others is yours to switch off on its own, ' +
    'here or from any message we send.',
  'mail.checking': 'Checking…',
  'mail.bouncing.hard':
    'Mail to {email} is being rejected, so we have stopped sending to it. Sign-in codes ' +
    'included — a code sent to a mailbox that refuses it reaches nobody.',
  'mail.bouncing.complaint':
    'Mail to {email} was reported as spam, so we have stopped sending to it. Sign-in codes ' +
    'included.',
  'mail.bouncing.diagnostic': 'What the mail server said: {diagnostic}',
  'mail.retry': 'I have fixed it — try again',
  'mail.retry.working': 'Trying…',
  'mail.retry.done': 'Done. Ask for a sign-in code and see whether it arrives.',
  'mail.retry.tooSoon': 'That was tried very recently. Wait an hour and try again.',
  'mail.retry.notSuppressed': 'Nothing is wrong with this address.',
  'mail.retry.noAddress': 'There is no address on this account yet.',
  /*
   * Two switches since F-124, and the copy is where the difference between them lives.
   *
   * F-122 put the nudge under the reports switch and wrote one label broad enough to cover
   * both ("Emails other than sign-in codes"). That label was honest about what the switch
   * did and dishonest about what it cost: somebody stopping end-of-day summaries was also
   * stopping the only mail that reaches an uninstalled learner, and no wording of a single
   * switch can offer that choice because there was only one choice to make.
   *
   * So each label now names its own thing, and neither claims anything about the other.
   *
   * **The nudge hint says the consequence plainly and does not argue.** Somebody who says
   * stop must be obeyed (CLAUDE.md rule 4), and a warning dressed as a threat is how a
   * person decides to press the spam button instead. It says what happens; it does not
   * tell them to install anything, because on iOS that instruction is unfollowable in a
   * browser tab.
   *
   * **And the reports hint says nothing is sent under it yet, because nothing is.** That
   * sentence was true in F-106, became false in F-122 when the nudge borrowed this switch,
   * and is true again now that the nudge has its own. A settings screen describing mail
   * nobody receives is how a learner decides the whole panel is decoration.
   */
  'mail.nudge.label': 'A nudge when a question is waiting and we cannot notify you',
  'mail.nudge.on': 'Send it',
  'mail.nudge.off': 'Do not send it',
  'mail.nudge.hint':
    'When there is a question for you and no device we can send a notification to, we send ' +
    'a short email instead: what is waiting and a link to it, never the question itself. ' +
    'Sign-in codes are not affected either way.',
  'mail.nudge.onlyChannel':
    'There is no device we can notify at the moment, so this email is the only way nabu can ' +
    'reach you. If you turn it off, nothing will arrive until you open the app yourself.',
  'mail.nudge.unreachable':
    'This is off and there is no device we can notify, so nothing will reach you at all: ' +
    'questions wait in the conversation until you open nabu. That is fine if it is what you ' +
    'want — you can turn it back on here whenever you like.',
  'mail.nudge.unreachableBouncing':
    'There is no device we can notify and mail to this address is being rejected, so ' +
    'nothing can reach you at all until the mailbox is working again — whatever these ' +
    'switches say.',
  'mail.reports.label': 'Summaries of how your studying is going',
  'mail.reports.on': 'Send them',
  'mail.reports.off': 'Do not send them',
  'mail.reports.hint':
    'End-of-day summaries and anything else we write about your progress. We do not send ' +
    'any of these yet; this is the switch that will stop them when we do. It has nothing to ' +
    'do with the nudge above.',
  'mail.switches.unsubscribe':
    'The unsubscribe link in any email we send stops both of these at once — it is one ' +
    'button in your mail app and it cannot ask which you meant. Sign-in codes are never ' +
    'affected, and you can turn either one back on here.',
  'mail.switches.suppressed':
    'While mail to this address is being rejected, none of it is sent whatever these ' +
    'switches say.',
  'mail.error.generic': 'Could not read this just now. Try again.',

  // ---- The page a one-click unsubscribe link lands on ----
  'unsub.title': 'Stopping these emails',
  'unsub.working': 'Recording that…',
  /*
   * It has to say that it stopped *everything*, because it did.
   *
   * One-click unsubscribe is a single action with no interface, so it cannot ask which
   * kind of mail somebody meant and stops all of it (see `oneClickOptOut` in
   * `@nabu/shared`). The only place that can be made honest is here, on the page the link
   * lands on — naming the nudge specifically, because that is the one with a consequence,
   * and pointing at the account screen where the two are separate. A confirmation that
   * quietly understated what it had done would be the trap this design is trying not to
   * set.
   */
  'unsub.done':
    'Done. We have stopped every email except sign-in codes — nobody can opt out of their ' +
    'own way in. That includes the nudge we send when a question is waiting and ' +
    'notifications cannot reach you, so if notifications are not working, nothing will ' +
    'reach you until you open the app. If you only meant to stop one kind, each of them has ' +
    'its own switch in your account.',
  'unsub.noLink':
    'This page needs the link from the email. Open it from the message, or change the ' +
    'setting from your account instead.',
  'unsub.home': 'Go to nabu',

  'session.signedIn': 'Signed in',
  /*
   * Which account this is (F-140). Sign-in is a code mailed to an address, so two addresses
   * are two accounts — with two plans and two schedules — and the app said nothing about
   * which one was open. `{email}` is substituted; with no address on file the app shows
   * `session.signedIn` instead and never invents one.
   */
  'session.as': 'Signed in as {email}',
  'session.signOut': 'Sign out',
  'session.objectives': 'My objectives',
  'session.chat': 'Conversation',

  'obj.new.title': 'What do you want to learn?',
  'obj.description.label': 'Describe it in your own words',
  'obj.description.placeholder':
    'For example: memorise the more important points across all meridians in TCM, including the five element points, source, connect, cleft, mu, shu, entry and exit — the number, name, location and description of each.',
  'obj.description.hint':
    'Write as much as you like. This exact text is what gets researched, so detail helps.',
  /*
   * Choosing where the material comes from (F-118).
   *
   * Leo, 2026-09-16: *"for my kids for example they dont need research just photos of the pages
   * in the books"*. So the question on screen is about what the learner *has*, never about which
   * pipeline runs: "I already have it" and "find it for me" are the two answers somebody can
   * give about their own situation, and neither of them mentions research, uploads, folding or a
   * plan being built. The words "research" and "web" appear only in the sentence under the
   * option that does it, where they describe what will happen rather than naming a mode.
   *
   * **No price, no tier, no hint of one.** Leo, 2026-09-15: *"the user should not know about
   * budget… they will pay a monthly flat fee so budget is our concern."* Uploads-only is the
   * cheapest thing this product can do and nothing here may suggest that is why it is offered.
   */
  'plan.source.label': 'Where should the material come from?',
  'plan.source.research': 'Find it for me',
  'plan.source.research.hint':
    'nabu reads up on the subject across the web and builds the plan from what it finds. You can add your own pages as well, any time.',
  'plan.source.uploads': 'I already have it',
  'plan.source.uploads.hint':
    'The plan is built only from the pages you add — photograph the pages of the book, or add a PDF. Nothing is looked up on the web.',
  /*
   * The third answer (F-119), in the learner's terms and never in the pipeline's.
   *
   * Leo asked for *"just research on the very specific subjects I am uploading"*, which is a
   * sentence about scope and not about a mode. The label says what the learner has and what they
   * want done about it; the hint says the two things that make it different from the other two
   * — the pages decide what the plan is about, and adding more later makes it look again — and
   * it says the second because that behaviour costs money silently and a learner who does not
   * expect it would find their plan growing for reasons they did not connect to anything.
   */
  'plan.source.scoped': 'I have some of it',
  'plan.source.scoped.hint':
    'The plan is built around the pages you add, and nabu reads up on what they cover and on what they assume you already know. Add more pages later and it looks again.',
  'obj.new.submit': 'Save it',
  'obj.working': 'Working…',
  'obj.target.label': 'How will you know you are done?',
  'obj.target.score': 'A score I want to hold',
  'obj.target.date': 'A date I need it by',
  'obj.target.scoreHint': 'how often you want to get them right',
  'obj.target.dateHint': 'intervals compress as the date approaches',
  'obj.list.title': 'Your objectives',
  'obj.list.loading': 'Loading…',
  'obj.list.empty': 'None yet.',
  'obj.status.drafting': 'Draft',
  'obj.status.researching': 'Researching',
  'obj.status.synthesising': 'Building the plan',
  'obj.status.review': 'Ready to review',
  'obj.status.active': 'Running',
  'obj.status.done': 'Done',
  'obj.status.abandoned': 'Abandoned',
  'obj.status.shelved': 'Set aside',
  'obj.current.description': 'What you asked for',
  'obj.target.save': 'Save target',
  'obj.target.saved': 'Saved.',
  'obj.close.done': 'I know this — mark it done',
  'obj.close.abandon': 'I am giving up on this',
  'obj.close.confirm': 'This closes the objective and lets you start another. Sure?',
  'obj.close.hint':
    'You work on one objective at a time. Closing this one opens the next — and nothing you close is lost, it goes to the list below and you can come back to it.',
  'obj.next.title': 'Ready for the next one',
  'obj.next.body': 'Your last objective is closed. Describe the next thing you want to learn.',

  'obj.history.title': 'Objectives you have closed',
  'obj.history.hint':
    'Nothing is running right now. Describe something new above, or pick one of these up again where you left it.',
  'obj.history.switchHint':
    'You have one running. Bringing another one back sets the current one aside — it keeps everything it has, lands in this list, and comes back the same way.',
  'obj.history.closed': 'closed',
  'obj.history.reactivate': 'Pick this up again',
  'obj.history.confirm':
    'This sets aside the objective you are on now and puts this one in its place. You are not giving up on it: it lands in this same list with everything it has, and you can come back to it whenever you want. Go ahead?',
  'obj.history.untitled': 'Objective without a title',
  'obj.history.switched': 'Done. The one you were on is set aside in this list.',
  /*
   * Said only when the plan that was set aside had research actually running.
   *
   * **It promises the one thing that is true and is not obvious**: nothing is cancelled and
   * nothing is charged twice. The pass finishes, its findings land on the plan in this list,
   * and the notification arrives when they do — so somebody who has just watched a progress
   * bar disappear knows their six dollars did not go with it. The alternative was silence,
   * which is the worst outcome available here.
   *
   * **The notification half is why this is not also said about a plan build**, which the
   * first draft did and a review caught. `research_ready` is sent even for an objective that
   * was closed mid-pass (`worthTelling` deliberately does not ask), and there is no
   * `curriculum_ready` anywhere in `CompletedEvent` — so "you will be told" is true of one
   * pass and a promise nothing keeps for the other.
   */
  'obj.history.switchedMidRun':
    'Done. The one you were on is set aside in this list — its research is still running, it finishes on its own, and you will be told when it is ready.',
  /*
   * The same moment for a plan that was being built rather than researched.
   *
   * Everything true of research is true here except the notification: assembly parks its
   * outcome on the shelved plan exactly as research does, so the plan comes back with its
   * curriculum. Nothing tells the learner when it lands, so this says where to look instead
   * of promising a notice that has no event behind it.
   */
  'obj.history.switchedMidBuild':
    'Done. The one you were on is set aside in this list — its plan is still being built, it finishes on its own, and the result will be waiting on it here.',
  'obj.history.resumed': 'Back on it.',

  'src.title': 'Your own material',
  'src.hint':
    'Optional. Photograph a page of your book, or add a PDF, and it will be read and used alongside the research.',
  /* The hint somebody sees when they arrived here from the gap notice. "Optional"
     is the wrong first word for the one thing on this screen only they can do. */
  'src.hint.wanted':
    'Your plan is missing a few things you asked for. A photograph of a page that covers any of them, or a PDF, is exactly what closes the gap.',
  'src.choose': 'Add pages or a PDF',
  'src.working': 'Reading it…',
  /* The promise the rest of F-92 keeps. It is said where the learner is standing when
     they finish adding a page, because that is the moment they used to go looking for
     the next button — and there were three of them. */
  'src.automatic':
    'Anything you add here goes into your plan on its own, and the questions for it are written straight after. You do not need to press anything else.',
  'src.empty': 'Nothing added.',
  'src.show': 'Show what was read',
  'src.hide': 'Hide',
  'src.loading': 'Loading…',
  'src.characters': 'characters',
  'src.truncated': 'truncated for display',
  'src.status.pending': 'Reading',
  'src.status.extracted': 'Read',
  'src.status.failed': 'Could not read',
  'src.error.type': 'That file type is not supported. Photographs and PDFs only.',
  'src.error.camera':
    'That is an iPhone photograph in HEIC, which cannot be read yet. In Settings → Camera → Formats, choose Most Compatible, or share the photo as a JPEG first.',
  'src.error.tooLarge': 'That file is too big. Photographs go up to',
  'src.error.upload': 'The upload did not complete. Try again.',
  'src.error.generic': 'Something went wrong with that file.',
  'src.failed.no_text': 'No text was found in this one. Try a straighter, brighter photograph.',
  'src.failed.too_large': 'This file is too big to read. Retake it at a smaller size.',
  'src.failed.unsupported_format': 'This file is not in a format that can be read.',
  'src.failed.too_many_pages': 'This PDF has too many pages. Add the chapter you are studying.',
  'src.failed.truncated':
    'One page of this holds more text than can be read in a single pass. A photograph of just that page usually works.',
  'src.retry': 'Try reading it again',
  'src.retrying': 'Trying again…',
  'src.remove': 'Remove',
  'src.removing': 'Removing…',
  'src.remove.confirm':
    'Remove this from your material? Anything read out of it goes too. Your plan keeps whatever it already learned from it until you rebuild.',
  'src.action.failed': 'That did not work. Try again.',
  'src.failed.internal': 'Something went wrong reading this one. It was not your file.',
  'obj.error.target': 'That target does not work — a date has to be in the future.',
  'obj.error.description': 'Say something about what you want to learn.',
  'obj.error.signIn': 'Sign in first.',
  'obj.error.generic': 'Something went wrong. Try again.',
  'obj.error.alreadyActive':
    'You are already running as many objectives as your plan allows. Close or shelve one to start another.',
  'obj.error.notFound': 'That objective is not there any more. The list has been reloaded.',
  'obj.error.notClosed':
    'That one is not closed after all. The list has been reloaded — have another look.',
  'obj.error.conflict':
    'Something moved while you were looking at that list. It has been reloaded — have another look.',

  'cur.rebuild': 'Build this plan again',
  'cur.rebuild.body':
    'Not right? Building it again replaces this plan from the same research — it takes a couple of minutes and does not repeat the expensive research step.',
  'session.checking': 'One moment…',
  'win.pause.label': 'Send me questions',
  'win.pause.running': 'Questions are being sent inside the hours below.',
  'win.pause.stopped':
    'Stopped. Nothing is being sent, and nothing is lost — your plan and your progress are exactly where you left them. Turn this back on whenever you want.',
  'win.pause.stoppedBySilence':
    'Stopped automatically after a few unanswered reminders. Nothing is lost — turn this back on, or reply “resume” in the chat.',
  'win.pause.failed': 'That did not save. Try again.',
  'start.title': 'Start learning',
  'start.body':
    'Your questions are ready. Starting means they begin arriving inside the hours you set — in the chat here, with a notification when one is waiting — and nothing arrives outside them.',
  'start.button': 'Start sending me questions',
  'start.confirm':
    'This starts sending you questions inside the hours you set. You can pause it whenever you want. Ready?',
  'start.working': 'Starting…',
  /*
   * **It is about sending, and it used to read as though it were about writing.**
   *
   * Rendered on the objective panel of any `active` plan, this said "Running — questions are on
   * their way", which is a true sentence about activation: the objective is live and the sweep
   * is sending inside the learner's hours. Four sections down, the questions panel was
   * separately offering to *write* the questions — and on 2026-09-19 it was doing that through a
   * live generation. Two true sentences, one screen, opposite meanings: *"it says questions are
   * on their way so its confusing"*.
   *
   * So this names the hours, which is the thing only activation can say and generation never
   * says. The two panels now divide the sentence between them rather than competing for it:
   * this one is about arrival, `items.writing.body` is about being written, and neither can be
   * read as the other.
   */
  'start.running': 'Running — questions arrive inside the hours you set.',
  'start.error.nothingActive':
    'Every subject is unticked, so there is nothing to send. Tick at least one above.',
  'start.error.noItems': 'There are no questions yet. Write them first, above.',
  /* Written as an invitation, never as an apology. The plan is not broken; it is
     incomplete in a way this learner, and only this learner, can close. */
  'gap.title': 'Things only you can add',
  'gap.body':
    'You asked for these and the research did not turn them up, so they are not in your plan and you will not be asked about them. A photograph of a page from your own book is what puts them in.',
  'gap.action': 'Upload a page that covers these',
  'gap.more': 'Show the rest',
  'gap.less': 'Show fewer',
  'gap.after.review':
    'Add the page first, then build the plan again further down: it re-reads everything, including what you just added, and this list gets shorter. It costs a couple of minutes and does not repeat the research.',
  /* Rewritten when folding became possible on a running objective. The old line
     said the plan stayed as it was until the next build, which was true then and
     is a lie now — and a notice that asks for a photograph and then says nothing
     will come of it is worse than no notice. */
  'gap.after.active':
    'Add the page first, then fold it in just below: it re-reads everything you have uploaded and adds what is new. Nothing you have already learned is lost, and your questions keep arriving the whole time.',

  /* The step after the upload, on an objective that is already sending.
     Every sentence here is load-bearing and each one answers a fear: that
     rebuilding costs the progress already made, that questions stop while it
     runs, and that this is instant. It is none of those, and saying so plainly
     is what makes the button safe to press. */
  'fold.title': 'Add what you have uploaded to your plan',
  'fold.body':
    'This re-reads everything you have added and folds anything new into the plan you are already working through. The topics you have been learning keep their place, and everything you have remembered about them stays — the plan only gains. Your questions carry on arriving throughout.',
  'fold.button': 'Add my uploads to the plan',
  'fold.working': 'Folding it in…',
  'fold.running':
    'Reading everything again and folding in what is new. A minute or two, and the questions for anything new are written after that. You can close this page — it carries on without you, and your questions keep coming.',
  'fold.error.running':
    'That is already happening — it started a moment ago and takes a minute or two.',
  'fold.error.notReady':
    'This objective has moved on since this page last looked at it. The page has been brought up to date.',
  'fold.failed':
    'The last attempt to add your material did not finish. Your plan and everything you have learned are exactly as they were — nothing was changed. What went wrong:',
  /* What the learner is told while the second half runs, and it exists because the
     second half used to be a button. Writing the questions is not a tidy-up after a
     fold — a topic with no questions is never sent — so this is the sentence that
     stops a plan that visibly gained a topic from being the end of the story. */
  'fold.writing':
    'Your plan has the new material. Writing the questions for it now — a few minutes, and only for what changed. You can close this page; it carries on without you.',
  /* The whole chain in one line, which is the entire point of doing it without asking.
     Leo, having pressed four buttons to get here: "the user should simply be told:
     these are the new topics and there are n new questions". */
  'fold.outcome':
    '{topics} new topics, {questions} new questions. They will start arriving with your usual ones.',
  /* Nothing was added but something was reworded, which is a real outcome and would
     otherwise be reported as "0 new topics". */
  'fold.reworded':
    '{topics} topics rewritten, {questions} new questions. They will start arriving with your usual ones.',
  /* Not a failure, and it must not read as one: re-reading material that is already in
     the plan is supposed to change nothing, and it costs nothing further. */
  'fold.nothingNew':
    'Everything you have added is already in your plan — there was nothing new to fold in, and no new questions were needed.',

  /* Asking for more research, which is the one control in this product that
     spends real money on being pressed. Every line here is written to be read
     *before* the press rather than explained after it: what it does, what it
     does not change, and — the sentence it would be easy to leave out — that it
     may come back with nothing new. See F-51 and docs/costs.md. */
  /*
   * The plan-level half of F-109's pair, and the copy now says which half it is.
   *
   * It used to describe "the same search again from your description", which is exactly what
   * the pass did and exactly why it was worth $2.38 to get the same document back. It now
   * looks for subjects that are *not* in your plan, is told what already is, and costs a
   * third as much. The other half — more on one subject that is already there — lives on that
   * subject, above.
   */
  'again.title': 'Something missing from your plan?',
  'again.body':
    'nabu goes back to the web looking for subjects your plan does not already cover. Whatever it finds is added to the material you already have: your plan, your questions and everything you have remembered stay exactly as they are until you fold the new material in yourself, further down.',
  'again.honest':
    'It is told what your plan already covers and asked only for what is not there, so it will not simply hand back the same material. If nothing is missing it says so and stops. To get more on a subject you already have, use the button on that subject instead.',
  'again.note.label': 'What is missing? (optional)',
  'again.note.placeholder': 'e.g. the point tables for the Five Shu',
  'again.button': 'Look for what is missing',
  'again.working': 'Starting…',
  'again.running':
    'Looking for what is missing. A minute or two to work out whether anything is, then each new subject is researched in the background. You can close the page — it carries on without you, you will get a notification, and nothing about your plan or your questions changes while it runs.',
  /* Said out loud rather than left as a dead button. The lock is released by age
     rather than by the pass finishing, so the button really is down for half an
     hour, and an unexplained disabled control is how somebody concludes the
     feature is broken and reloads until it works. */
  'again.locked':
    'This button comes back half an hour after a pass starts, whether or not the pass has finished. That is deliberate on the heaviest thing here: nothing can start it twice by accident.',
  'again.confirm':
    'This runs a fresh research pass. It adds to the material you already have and, on its own, changes nothing about your plan or your progress. Go ahead?',
  'again.error.running':
    'Something is already being researched on this objective. Give it a minute — or an hour, if it is already onto the subjects.',
  'again.error.notReady':
    'This objective is not somewhere a second research pass can start from. The page has been brought up to date.',
  'again.next': 'When it lands, fold it into your plan below. Nothing changes until you do.',

  /* Leaving. The whole of this panel is written on the assumption that somebody
     reading it is about to lose 476 review states and will not get them back, so
     nothing here is softened and nothing is implied. The one thing that is kept
     is named, because "we delete everything" with an unmentioned exception is the
     kind of sentence a privacy right is exercised against. */
  'del.title': 'Delete everything',
  'del.body':
    'This erases your account and everything in it: every objective, the plans built for them, every question written for you, everything you have remembered, your whole message history, the pages you photographed and the text read out of them, and the link between your email address and this account. The messages stop the moment it runs.',
  'del.irreversible':
    'There is no undo and there is no copy. Nothing is archived, nothing can be recovered by asking, and signing in again with the same address starts you from nothing.',
  'del.kept':
    'One thing is kept: the monthly count of what nabu spent on model calls for this account. It holds no text — nothing you wrote, nothing you uploaded, only numbers, under an id that no longer belongs to anybody — and it is what the bill is checked against.',
  'del.ack': 'I understand this cannot be undone.',
  'del.button': 'Delete my account and everything in it',
  'del.working': 'Deleting…',
  'del.confirm':
    'Last check. This erases your objectives, your plans, your questions, everything you have remembered, your messages and the pages you uploaded. It cannot be undone. Delete it all?',
  'del.done.title': 'It is gone',
  'del.done.body': 'Your account and everything in it has been deleted, and you are signed out.',
  'del.done.counts':
    '{objectives} objectives, {nodes} topics, {items} questions, {reviews} things you had remembered, {messages} messages, and {objectVersions} stored files — counting every earlier version of them.',
  'del.done.home': 'Back to the start',
  /* A failure here is the one place a reassuring message would be a lie: a
     deletion that stopped halfway really has destroyed some of it. Saying so is
     the only honest option, and "try again, it carries on" is the only useful
     instruction. */
  'del.failed':
    'The deletion did not finish. Some of your data is already gone and some may still be there — this did not stop cleanly. Try again: it carries on from where it stopped. If it keeps failing, say so rather than assuming it worked.',
  'del.error.notFound': 'There is no account here to delete. You may already have deleted it.',
  'del.error.notConfirmed': 'That did not arrive confirmed. Tick the box and try again.',

  'res.title': 'What the research found',
  'res.hint':
    'This is the exact text your curriculum is built from. The end of it lists what the research could not find — worth a look before you build.',
  'res.characters': 'characters',
  'res.truncated': 'cut off here; the whole thing was used to build the curriculum',

  /*
   * What this section says when it has nothing, which it never had to say before (F-141).
   *
   * On a route of its own it could render nothing at all and the learner would simply have
   * found an empty screen. As a section of the plan it sits between the outline and the
   * uploader, under a heading, and a heading over silence reads as something that failed to
   * load. Both sentences are facts about this objective rather than apologies: one has had
   * nothing looked up yet, the other is not going to have anything looked up because that is
   * what the learner chose.
   */
  'res.empty.none':
    'Nothing has been read about this yet. Once research starts, every subject it looks at shows up here as it lands.',
  'res.empty.uploads':
    'This plan is built from the pages you add, so nothing is being looked up on the web. What you add appears under your material, below.',

  /*
   * F-109, one contiguous block: research that happens in passes, and is watched.
   *
   * The copy carries two things a spinner cannot. **What is happening** — outlining is
   * minutes, deepening is a batch and may be an hour — and **what each subject's state
   * means**, because "thin" and "not paid for" look identical on screen and want opposite
   * reactions from the learner.
   */
  'res.progress.title': 'Research in progress',
  'res.progress.outlining': 'Finding out what this subject is made of',
  'res.progress.deepening': 'Researching each subject in turn',
  'res.progress.done': 'Research finished',
  'res.progress.failed': 'Research stopped',
  'res.progress.of': '{done} of {total} subjects researched',
  'res.progress.slow':
    'Each subject is researched in the background, which can take up to an hour. You can close this page — you will get a notification when it is done.',
  /*
   * `res.progress.check` was here and is gone (F-141). It resumed a poll that lived in
   * `Research.tsx`; the merged screen has one poll, in `Objectives.tsx`, and one button that
   * resumes it — `obj.wait.check`, which every other long wait on this screen already shares.
   */
  'res.state.outlined': 'waiting',
  'res.state.queued': 'queued',
  'res.state.researching': 'reading',
  'res.state.deep': 'researched',
  'res.state.thin': 'little found',
  // Not "failed" and not "empty". It says what happened — a search that found nothing — rather
  // than passing a judgement on the subject or implying something of ours broke.
  'res.state.barren': 'nothing found',
  'res.state.unfunded': 'not covered',
  'res.state.failed': 'failed',
  'res.missing': 'Not found:',
  'res.deeper': 'Go deeper',
  'res.deeper.working': 'Starting…',
  'res.deeper.confirm':
    'This researches this one subject more deeply. It adds to your material and changes nothing about your plan or your progress. Go ahead?',
  'res.error.running': 'Something is already being researched on this objective. Give it a minute.',
  'res.error.notReady': 'This objective is not somewhere more research can start from.',
  'res.error.noSubject': 'That subject is not there any more. Reload the page.',
  'res.error.generic': 'That did not start. Try again in a moment.',
  // Said when a subject has been started more than once. The row counts attempts, including
  // the ones that ran out of clock and wrote nothing, and "this is the second go" is the only
  // honest explanation for a subject sitting at *reading* twice as long as its neighbours.
  'res.attempts': 'attempt {attempts}',

  /*
   * F-144, F-147 and F-149, one contiguous block: a sixteen-minute wait that says what it is doing.
   *
   * **The copy has one job and it is retention.** Leo, 2026-09-17: *"a user would just drop
   * here."* Everything below is written against a person who has just pressed a button and has
   * nothing to do for a quarter of an hour, so every line names something that is actually
   * happening — a count off a row, a number of characters that was written, a paragraph out of
   * their own document — and none of it is reassurance.
   *
   * The five group names are the vocabulary the whole panel rests on, and they are the
   * learner's rather than the pipeline's: `outlined` and `queued` are both "still to come",
   * `deep` and `thin` are both "researched" because both produced material that reaches the
   * plan, and "nothing found" is kept apart from "not reached" because one is a fact about the
   * subject and the other is a fact about us.
   */
  'pulse.starting.body':
    'Started. nabu is working out what your subject is made of — the first subjects appear here in a minute or two, and then fill in one at a time.',
  'pulse.reading': 'being read now',
  'pulse.waiting': 'still to come',
  'pulse.found': 'researched',
  'pulse.empty': 'nothing found',
  'pulse.stalled': 'not reached',
  'pulse.chart.caption': '{characters} characters of material, over {minutes} minutes',
  'pulse.chart.alt':
    'How the material arrived over the run: {characters} characters in {minutes} minutes.',
  'pulse.quote.from': 'From {subject}',
  'pulse.findings': '{found} of {subjects} subjects researched · {characters} characters read',
  'pulse.findings.gaps': '{gaps} of them said what they could not find.',
  'pulse.seeAll': 'See everything that was read',

  /*
   * The looping carousel on the researching face (F-141).
   *
   * **The copy has one job: to make it obvious that every card is the learner's own subject**,
   * and not a generic message about waiting. The four labels are the four things a card can be,
   * and the difference between them is what upgrading in place looks like — "what you will
   * learn" comes from the outline that exists at forty seconds, "just found" from a research
   * pass that has landed since, and "not found yet" is F-126's `missing` sentence, which is
   * honest and more interesting than a gap.
   */
  'carousel.roledescription': 'carousel',
  'carousel.label': 'What is being read up on, a card at a time',
  'carousel.kind.outline': 'In your plan',
  'carousel.kind.promise': 'What you will learn',
  'carousel.kind.finding': 'Just found',
  'carousel.kind.gap': 'Still looking for',
  'carousel.from': '{title} · subject {place} of {subjects}',
  'carousel.read': '{characters} characters read',
  'carousel.prev': 'Previous card',
  'carousel.next': 'Next card',
  'carousel.pause': 'Pause',
  'carousel.resume': 'Start again',
  'carousel.position': '{place} of {total}',

  /*
   * F-129, one contiguous block: what happened to every subject, and which of them reached the
   * plan.
   *
   * **The copy carries an argument and not just information.** The report behind it exists
   * because a learner had nine subjects researched and a plan of five, with nothing anywhere
   * relating the two lists. Three rules run through every line below, and each of them is a
   * thing this product got wrong before:
   *
   * - **"Nothing was found" is a fact about the world, not a fault of ours.** People will use
   *   this to learn about everything, including subjects with almost nothing written about them.
   *   So the barren copy explains and never apologises, and nothing here renders in the error
   *   tint — see `.gaps` in styles.css, which exists for exactly this distinction.
   * - **A short plan is a product.** It says what it covers, names what is missing, and offers
   *   the two things that can change the answer. It does not call itself a failure.
   * - **Two remedies, because there are only two.** A page from the learner's own book, or
   *   another look with different angles. Everything else is a button that cannot help.
   */
  'cov.summary': '{researched} subjects researched · {inPlan} in your plan',
  'cov.summary.missing': '{missing} did not reach it. Each one says why.',
  'cov.summary.pending': '{pending} still being read.',
  'cov.summary.unknown':
    'This plan was built before nabu kept a record of which subject each topic came from, so the two lists cannot be matched up here. The next plan it builds can be.',
  'cov.outcome.deep': 'researched',
  'cov.outcome.thin': 'little found',
  'cov.outcome.barren': 'nothing found',
  'cov.inPlan.one': '1 topic in your plan',
  'cov.inPlan.many': '{nodes} topics in your plan',
  'cov.notInPlan': 'Not in your plan',
  // The three fallbacks, used when the pass that made the decision left no words of its own —
  // which is every subject of an objective researched before this existed. "Not in your plan"
  // with nothing after it is the state the whole report was written to abolish.
  'cov.why.deep':
    'It was researched, and building the plan did not draw on it. Adding a page of your own, or looking again, is what changes that.',
  'cov.why.thin':
    'Something was found and it does not cover the subject, so there was not enough here to teach from.',
  'cov.why.barren':
    'Nothing usable came back for this one. Some subjects have very little written about them anywhere, and that is a fact about the subject rather than a fault.',
  'cov.remedy.upload': 'Add a page of your own',
  'cov.remedy.again': 'Look again for this one',
  'cov.missing.title': 'What did not reach your plan',
  'cov.missing.body':
    'Each of these was researched and is not in the plan. What is missing from each one is below it, in the words of the pass that looked.',
  'cov.thin.title': 'A short plan, and why it is short',
  // Names the two remedies in the sentence rather than growing two buttons, because both of
  // them live on the research screen and a control here could only scroll to a panel that is
  // not on this route. See the notice in Curriculum.tsx.
  'cov.thin.body':
    'Your plan covers {inPlan} of the {researched} subjects that were researched. That is what there was to build from, and the subjects it does cover are worth studying. Adding a page from a book you have, or asking nabu to look again at one subject, is what can change the rest.',
  'cov.thin.action': 'See every subject',
  'cov.unattributed':
    'This plan was built before nabu recorded which subject each topic came from, so it cannot show you here which subjects are behind it. Nothing is missing from the plan itself.',
  // On a topic, naming the subject it was built from. Short, because it rides beside a title
  // that may already be a line and a half of Spanish.
  'cov.from': 'From {subject}',
  'cov.more': 'Show the rest',
  'cov.less': 'Show fewer',

  'items.title': 'Your questions',
  'items.build.body':
    'The plan above is what you will be asked about. Writing them happens in the background and takes longer the bigger the plan is — you can close this and come back.',
  'items.build': 'Write my questions',
  'items.working': 'Writing them…',
  'items.ready': 'Ready',
  'items.rebuild': 'Write them again',
  'items.failed.body': 'The questions did not get written.',
  /*
   * The state the panel could not say, and the reason this feature exists.
   *
   * Leo, 2026-09-19: *"do i need to click write my questions, its gotten to a plan but i dont
   * know if questions are being written or not and it still shows me that button"*. Both halves
   * of that are answered here and the second one matters most: the body says the button has
   * already been pressed — by him or by the plan itself — so there is nothing left to do.
   *
   * It says "a big plan takes a while" rather than a number of minutes, and `items.build.body`
   * was corrected to match. Generations on `nabu-generate-items` have run from about a minute to
   * 25 minutes 14 seconds, which is the 193-topic one this feature came from; a screen that
   * promises "a few minutes" over that end of the range is the same lie in a quieter voice, and
   * it is the one the learner reads *before* pressing.
   */
  'items.writing': 'Writing them',
  'items.writing.body':
    'Your questions are being written right now — nothing more to press. A big plan takes a while, it happens in the background, and nothing is lost if you close this. They appear here when they are done.',
  /*
   * Refused because the research that fills the plan is still arriving (F-114 follow-up). It
   * is not an error and must not read like one: the plan builds itself when there is enough,
   * and the only thing being declined is building it early.
   */
  'plan.error.stillResearching':
    'Still reading up on this — the plan builds itself as the research lands, and you will be told when it is ready.',
  'items.error.notReady':
    'There is no curriculum to write questions about yet. Build the plan above first.',
  'items.error.alreadyRunning':
    'They are already being written for this plan. Give it a while — this page will say when they are done.',
  'items.folding':
    'Your plan is being updated right now. The questions can be written as soon as that finishes.',

  'obj.error.notDrafting':
    'The research has already been started for this one. The page has been brought up to date.',
  'obj.error.notReady': 'This objective is not ready for a curriculum yet.',
  'obj.error.noMaterial':
    'There is nothing to build a curriculum from yet — no research has landed, and nothing you added has been read.',

  /*
   * The uploads-only objective with nothing read yet, which is a legitimate resting state.
   *
   * **It is not a wait and it is not an error**, and copy that implied either would be the whole
   * of getting this feature wrong: the learner said they have the material, so nothing is
   * supposed to be happening until they add it. It reads as an invitation with one thing to
   * press, and it says plainly that nothing is being looked up — otherwise somebody who chose
   * this deliberately has no confirmation that the choice took.
   *
   * The second sentence exists because the same panel is on screen while extraction runs: a
   * learner who has just added eight photographs would otherwise read "add your pages" as though
   * nothing had happened. Saying both is honest at both moments and needs no state to decide.
   */
  'obj.material.title': 'Add your material',
  'obj.material.body':
    'This plan gets built from what you add — photograph the pages of the book, or add a PDF. A couple of pages is usually enough to start, and the plan appears here on its own once they have been read. Nothing is being looked up on the web.',
  'obj.material.button': 'Add pages',
  'obj.material.switch':
    'Changed your mind? You can ask nabu to research the subject instead, under Settings.',
  /*
   * The same resting state for a scoped objective, and the difference is one sentence.
   *
   * The uploads-only copy ends "Nothing is being looked up on the web", which is the confirmation
   * that the choice took. For a scoped objective that sentence is false, and leaving it would be
   * the interface contradicting what the plan then does an hour later. What replaces it says both
   * halves of the actual bargain: nothing is looked up *yet*, and adding a page is what sets it
   * off — so a learner watching an empty screen knows the next move is theirs.
   */
  'obj.material.body.scoped':
    'This plan gets built around what you add — photograph the pages of the book, or add a PDF. A couple of pages is usually enough to start. Once they have been read, the plan appears here and nabu goes and reads up on what they cover. Nothing is looked up until then.',
  'obj.material.switch.scoped':
    'Changed your mind? You can ask nabu to research the whole subject, or to use only your pages, under Settings.',
  'obj.error.noResearch':
    'This objective is built from your own material, so there is nothing to research. You can change that under Settings.',
  /*
   * Reachable only from a stale screen, and it still has to be right.
   *
   * The research button is not rendered for a scoped objective, so nobody arrives here by
   * pressing what they were offered. What they need to read is that nothing is broken and what
   * the actual next move is — not "this objective is not a draft", which is what the refusal said
   * before F-119 gave it a reason of its own, and which sends somebody to reload a page that will
   * show them the same thing.
   */
  'obj.error.awaitingMaterial':
    'This plan gets built from your own pages. Add one and nabu starts reading up on it by itself.',
  'obj.error.busy': 'Something is running on this objective right now. Try again in a minute.',

  /*
   * The monthly research grant, spent (F-151).
   *
   * **It names the limit and when it lifts**, because the design says a refusal a learner reaches
   * is a bug and the least this can do is not be a mystery. "Something went wrong" was what this
   * said before, which is false — nothing went wrong, they used what their plan includes — and it
   * invites the one reaction that cannot help, which is pressing the button again.
   *
   * It does not name a number of runs. The count is on the answer the app already holds
   * (`/api/auth/me`), but nothing renders it yet, and a sentence claiming "you have 0 of 1 left"
   * from a screen that cannot show the other 364 days of that arithmetic would be a promise this
   * release does not keep.
   */
  'obj.error.researchGrantSpent':
    'You have used this month’s research. Your plan includes a set number of research passes each month, and it starts again when the month turns.',

  /*
   * Changing the choice later, on the settings screen.
   *
   * Two things have to be said and both are about what pressing this does *not* do. It does not
   * start a research pass — a setting that spent money on being saved is the button this app has
   * always declined to build — and it does not take anything away from a plan that already
   * exists, which is the fear somebody switching has about the work they have already done.
   */
  'cfg.source.title': 'Where the material comes from',
  'cfg.source.body':
    'You can change this at any time, and nothing you already have is lost. Choosing to have nabu find the material does not start it looking — the plan screen offers that when you are ready.',
  'cfg.source.save': 'Save this',
  'cfg.source.saved': 'Saved.',

  'obj.research.title': 'Research this',
  'obj.research.body':
    'nabu reads up on the subject across the web and writes down what it finds, with the source of every claim. It takes five to fifteen minutes, and it runs once — this is the most expensive thing nabu does, so start it when the description above says what you mean.',
  'obj.research.start': 'Start the research',
  'obj.research.starting': 'Starting…',
  'obj.research.running.title': 'Reading up on it',
  /* A minute or two, not fifteen. This step is now only the first pass — the one that finds
     out what the subject is made of — and the panel above it shows each subject filling in
     afterwards, on its own clock. Naming the old figure here would make the fast half look
     broken. */
  'obj.research.running.body':
    'A minute or two to work out what your subject is made of. The subjects appear above as it finds them, and each one is then researched properly in the background — that part can take up to an hour, and you will get a notification when it is done.',
  'obj.wait.slow':
    'Still going, and this page has stopped checking rather than sit here refreshing all afternoon. Nothing is lost.',
  'obj.wait.check': 'Check again',
  'obj.research.failed.title': 'The research stopped',
  'obj.research.failed.body':
    'It did not get far enough to leave anything behind, so the objective is a draft again. What went wrong:',
  'obj.research.retry': 'Start the research again',

  'cur.title': 'The plan',
  'cur.build.body':
    'The findings are in. Building the curriculum turns everything read so far into an ordered set of topics — a minute or two.',
  'cur.build': 'Build the curriculum',
  'cur.failed.body':
    'The last attempt to build the curriculum did not finish. Everything that was read is still there, so trying again pays for the build and not the research. What went wrong:',
  'cur.building.title': 'Building the curriculum',
  'cur.building.body':
    'One pass over everything read so far. Usually a minute or two, and the plan appears here when it is done.',
  'cur.empty': 'No plan yet.',
  'cur.topics': 'topics',
  'cur.weight.label': 'The band on each topic is how much of the plan it is worth — not how often you will be asked.',
  'cur.weight.core': 'Core',
  'cur.weight.major': 'Important',
  'cur.weight.minor': 'Supporting',
  'cur.weight.aside': 'On the edge',
  'cur.subjects': 'subjects',
  'cur.inside.one': 'topic inside',
  'cur.inside.many': 'topics inside',
  'cur.sections.openAll': 'Open every subject',
  'cur.sections.closeAll': 'Close every subject',
  'cur.active.hint':
    'Every subject starts ticked. Untick one and nabu writes no questions for it and never asks you about it — the rest of the plan carries on as it is.',
  'cur.active.aria': 'Study this subject',
  'cur.active.off': 'Not studying',
  'cur.active.failed':
    'That did not save, so it is back to how nabu still has it. Try again.',
  'cur.active.none':
    'Nothing is ticked, so there is nothing left for nabu to ask you about. Tick at least one subject.',
  'cur.summaries.show': 'Show the full descriptions',
  'cur.summaries.hide': 'Shorten the descriptions',
  'cur.untitled': 'Topic without a title',

  'prog.title': 'Where you are',
  'prog.loading': 'Loading…',
  'prog.unreadable':
    'Your progress could not be read. That is a fault here and not a comment on how you are doing — reload the page.',
  'prog.headline': 'of the plan, weighted by how much each subject is worth',
  'prog.headline.none':
    'There is nothing to measure yet: no questions have been written for the subjects you are studying.',
  'prog.counts':
    '{holding} held · {struggling} to work on · {untested} not asked yet, out of {questions} questions.',
  'prog.answered.none':
    'Nothing has been asked yet, so nothing is known about how you are doing. That is an empty result, not a bad one.',
  'prog.answered':
    'You have answered {answered} of {questions} questions. Across those {answered}, and only those, nabu puts your chance of getting one right today at {recall}.',
  'prog.pace.score':
    'There is no date to be on track for. You asked to hold {target}, which is the standard nabu schedules against.',
  'prog.pace.date.ok':
    '{days} days left. The plan needs about {required} questions a day and you accept up to {capacity}, so nothing about it is impossible yet — which is not the same as a promise.',
  'prog.pace.date.tight':
    '{days} days left. The plan needs about {required} questions a day and you accept {capacity}. It does not fit: move the date, untick some subjects, or take more questions a day.',
  'prog.load.title': 'Today',
  'prog.load':
    'Up to {limit} questions today. {sent} asked, {answered} answered, and {due} ready to ask.',
  'prog.load.noLimit':
    'You have not saved a schedule yet, so there is no daily cap of your own to report against.',
  'prog.load.ceiling':
    'nabu asks one question at a time and waits for your answer, so {limit} a day is a ceiling and not a forecast. Most days will be fewer. The short lessons that teach a topic, and any nudge about a question still waiting, do not count against it.',
  'prog.load.waiting': 'One question is outstanding. Nothing new goes out until you answer it.',
  'prog.load.paused': 'Sending is stopped, so nothing is going out at all.',
  'prog.load.pausedBySilence':
    'Sending stopped by itself after three reminders went unanswered. Turn it back on below and everything picks up where it was.',
  'prog.load.noZone':
    'We do not know your timezone, so nabu cannot tell which day it is where you are — the count above may be against the wrong day.',
  'prog.subjects.title': 'Subject by subject',
  'prog.subjects.byPlan': 'Show them in plan order',
  'prog.subjects.byWeakest': 'Show the weakest first',
  'prog.subject.counts':
    '{holding} of {questions} held · {struggling} to work on · {untested} not asked',
  'prog.subject.none': 'No questions yet',
  'prog.note.explanations':
    '{count} of the messages in this plan are explanations rather than questions. Nobody answers those, so they are left out of every number above.',
  'prog.note.noQuestions':
    '{count} subjects you are studying have no questions written for them. They are left out of the figure above rather than counted as nought.',
  'prog.note.unplaced':
    '{count} questions belong to topics that are no longer in the plan. They are still asked, and they are not counted under any subject.',
  'prog.note.off':
    'A subject you unticked after starting still has its questions in the rotation — unticking stops new ones being written, not ones already scheduled.',
  'prog.note.partial':
    'There are more questions here than this screen reads at once, so every number above is a floor rather than a total.',

  'sheet.title': 'What you have learned',
  'sheet.hint':
    'One line per fact you have actually been asked and answered — not the whole plan, and nothing you have not been taught yet. Made to be printed.',
  'sheet.download': 'Download it as a PDF',
  'sheet.working': 'Writing it…',
  'sheet.error.empty':
    'Nothing has been answered yet, so there is nothing to put on a sheet. It fills up as you answer questions.',
  'sheet.error.rewrite':
    'The sheet could not be written this time. Nothing is lost — try again in a moment and it will pick up where it stopped.',
  'sheet.error.generic': 'The sheet could not be downloaded. Try again.',

  'win.title': 'When nabu may reach you',
  'win.hint':
    'This is a standing arrangement, not part of any one objective — it stays as you set it when you close one and start another.',
  'win.loading': 'Loading…',
  'win.defaults': 'Nothing saved yet, so these are the defaults.',
  'win.limit.label': 'How many questions a day, at most',
  'win.limit.unit': 'questions a day',
  'win.hours.label': 'The hours you are reachable',
  'win.hours.from': 'From',
  'win.hours.until': 'Until',
  'win.hours.hint':
    'Outside these hours nothing is sent — it waits. Both times are read on your own clock.',
  'win.weighting.label': 'How that number is shared between your plans',
  'win.weighting.even': 'Evenly',
  'win.weighting.even.hint':
    'Every plan gets the same share of your questions. Whichever one has waited longest is asked next.',
  'win.weighting.weighted': 'Deadlines first',
  'win.weighting.weighted.hint':
    'A plan gets a bigger share the more it still has to cover before its date — so a crowded exam in two weeks is asked more often than an open-ended plan. Nothing is ever dropped: every plan with work still comes round.',
  'win.weighting.onePlan':
    'You have one plan running, so there is nothing to share out yet. This starts to matter when you have two.',
  'win.weighting.noDeadlines':
    'None of your plans has a date on it, so "deadlines first" works out exactly the same as "evenly" — there is nothing to be behind on. Set a date on a plan and this starts to do something.',
  'win.weighting.inForce':
    'At least one of your plans has a date, so this setting is deciding which of them you hear from more often.',
  'win.explanations.label': 'Send explanations, not only questions',
  'win.explanations.hint':
    'Off, and nabu only ever asks you things. On, and it teaches you a topic in a few hundred words before asking about it, and teaches it again if you start missing. These do not count against your daily questions. Either way a wrong answer is still corrected.',
  'win.plain': '{count} questions a day between {from} and {until} — about one every {gap}.',
  'win.plain.one': 'One question a day, somewhere between {from} and {until}.',
  'win.plain.quiet': 'Nothing at all between {until} and {from}.',
  'win.plain.explanations.on':
    'Short lessons arrive on top of that, as many as the material needs — they are how a topic is taught before it is asked about, so they do not use up your questions.',
  'win.plain.explanations.off': 'Questions only. Nothing is sent to read first.',
  'win.unit.hours': 'h',
  'win.unit.minutes': 'min',
  'win.zone': 'Read on the clock where you are:',
  'win.zone.missing':
    'We do not know which timezone you are in, so these hours cannot be honoured yet — hours without a timezone are not a schedule. Sign in again from the phone you use and it gets picked up.',
  'win.save': 'Save this',
  'win.saved': 'Saved.',
  'win.working': 'Saving…',
  'win.error.load': 'Could not read your current schedule. Reload the page.',
  'win.error.limit': 'Between 1 and 24 questions a day. Zero is not a schedule — close the objective instead.',
  'win.error.limitWhole': 'A whole number of questions a day.',
  'win.error.time': 'Those hours could not be read. Use the two time pickers.',
  'win.error.empty': 'The start and the end are the same time, which leaves no hours to reach you in.',
  'win.error.wrap':
    'The hours have to start and finish on the same day. If you want to be reached late at night, end at 23:59 rather than after midnight.',
  'win.error.weighting':
    'That way of sharing out your questions is not one this version knows. Pick one of the two above and save again.',
  'win.error.generic': 'That did not save. Try again.',

  /*
   * The conversation (F-100).
   *
   * `chat.hint` names three words the learner may type — and they are copy here rather than
   * imported, because `MORE_FORM`, `DISPUTE_WORD` and the `n más preguntas` form live in
   * `packages/messaging` beside the recognisers that read them, and pulling that package
   * into a browser bundle would ship the AWS SDK to a phone for the sake of three strings.
   * Renaming one of those words means editing this line; grep for the constants.
   */
  'chat.title': 'The conversation',
  'chat.loading': 'Reading the conversation…',
  'chat.empty':
    'Nothing yet. The first question arrives once an objective is running and you are inside the hours you chose.',
  'chat.older': 'Show earlier messages',
  'chat.input.label': 'Your answer',
  'chat.input.placeholder': 'Answer the question, or ask for something.',
  /* Said only to a screen reader — the key itself says it to everybody else through
     `enterkeyhint`, and a visible line of instructions under the box would cost the thread a
     line of conversation on every screen to explain a convention people already have. */
  'chat.input.hint': 'Enter sends. Shift and Enter start a new line.',
  'chat.send': 'Send',
  'chat.sending': 'Sending…',
  'chat.retry': 'Send it again',
  'chat.said.you': 'You said:',
  'chat.said.nabu': 'nabu said:',
  'chat.error.load': 'The conversation could not be read. Reload the page.',
  'chat.error.empty': 'There is nothing to send.',
  'chat.error.long':
    'That is longer than the conversation can keep, so it would be stored differently from how it was read. Shorten it and send it again.',
  'chat.error.session': 'This session is no longer valid. Sign in again.',
  'chat.error.graded':
    'That answer was marked, but the reply did not come back. Sending it again would not help — the question is closed and the next one still arrives.',
  'chat.error.send': 'That did not send. Try again.',
  /*
   * F-151: the chat is per plan, so somebody with no plan has no conversation to type into.
   *
   * The one thing this must not say is "try again", which is what it said before it had copy
   * of its own — advice that can never work, because retrying without a plan produces the same
   * refusal every time. It names the thing they actually have to do next instead.
   */
  'chat.error.no_objective':
    'There is no plan to talk about yet. Tell nabu what you want to learn and the questions start from there.',
  'chat.error.offline': 'No connection. Try again once you have one.',

  /*
   * The conversation as a screen rather than a panel (F-109).
   *
   * Two groups of copy that are not like the rest of the dictionary.
   *
   * `chat.kind.*` and `chat.mark.*` name what a message *is* — the same distinction F-68's
   * marker emoji make in a notification, in words, for a screen that has room for both. They are
   * the log's `purpose` values collapsed onto seven, and `chat/kinds.ts` owns the mapping.
   *
   * `chat.word.*` are not labels. They are the exact text a chip puts in the box, and the
   * system reads them back: `more details`/`más detalles` is `MORE_FORM`,
   * `dispute`/`reclamo` is `DISPUTE_WORD`, and `5 more questions` is the form
   * `moreQuestions.ts` parses. They are copy here rather than imported for the reason
   * `chat.hint` already gives — those constants live in `packages/messaging` beside the AWS
   * SDK, and a browser bundle has no business carrying a DynamoDB client for four strings.
   * **Changing one of those constants means changing these, and a chip that fills the box
   * with a word nothing recognises is silent: the message is graded as an answer instead.**
   * `chat.chip.*` beside each one is the button's own label, which is free to be a sentence.
   *
   * **Lower case, and with no quotation marks, and the two facts have different reasons.**
   * Lower case because Leo read the capitals in the app for what they looked like — *"code
   * words are all in caps it looks bad in the ui looks like im yelling"*, 2026-09-16 — and
   * they were only ever a WhatsApp emphasis (`packages/messaging/src/commands.ts` is the
   * whole argument). No quotation marks because this string is typed, not cited: our own
   * copy names a command as `“more details”`, and a chip that filled the box with the quotes
   * would put them in the learner's own message bubble. `plain` folds them away either way,
   * so both forms parse — this is about what the thread looks like, not about what works.
   */
  'chat.close': 'Close',
  'chat.day.today': 'Today',
  'chat.day.yesterday': 'Yesterday',
  'chat.older.loading': 'Reading earlier messages…',
  'chat.older.end': 'This is the beginning.',
  'chat.older.again': 'That did not load. Tap to try again.',
  'chat.new.one': '1 new message',
  'chat.new.many': '{n} new messages',

  'chat.kind.said': 'You',
  'chat.kind.briefing': 'Briefing',
  'chat.kind.question': 'Question',
  'chat.kind.reminder': 'Reminder',
  'chat.kind.mark': 'Marked',
  'chat.kind.reply': 'nabu',
  'chat.kind.summary': 'Your day',
  'chat.kind.notice': 'Notice',
  'chat.mark.wrong': 'Not right',
  'chat.mark.partial': 'Partly right',
  'chat.mark.correct': 'Right',
  'chat.mark.effortless': 'Right, easily',

  'chat.head.loading': 'Reading…',
  'chat.head.untitled': 'Your objective',
  'chat.head.asked': 'What you asked for',
  'chat.head.noObjective': 'Nothing is running yet. State an objective and the questions start.',
  'chat.head.byDate': 'Needed by',
  'chat.head.toHold': 'Aiming to hold',
  'chat.head.tapProgress': 'Show how you are doing',
  'chat.head.progress': 'How you are doing',

  'chat.prog.mastered': 'mastered, across the whole objective',
  'chat.prog.holding': 'holding',
  'chat.prog.struggling': 'struggling',
  'chat.prog.untested': 'not asked yet',
  'chat.prog.due': 'due now',
  'chat.prog.today': 'answered today',
  'chat.prog.of': 'of {n} questions.',
  'chat.prog.none': 'Nothing to measure yet. Progress starts once an objective is running and you have answered something.',
  'chat.prog.nothing': 'Nothing answered yet, so there is nothing to average.',
  'chat.prog.paused': 'Questions are paused. Send “resume” in the chat to start again.',
  'chat.prog.partial': 'Your curriculum is large enough that this read stopped early, so these counts are a floor rather than a total.',

  'chat.subject.title': 'Where this came from',
  'chat.subject.untitled': 'Untitled subject',
  'chat.subject.unmeasured': 'Nothing answered in this subject yet.',
  'chat.subject.inactive': 'You unticked this subject on the plan. Questions already scheduled still arrive.',
  'chat.subject.gone': 'This came from an objective you are not studying now, so there is no current progress for it.',

  /*
   * The sources behind a message (F-116).
   *
   * Leo asked to tap a briefing and see where the data came from, and the copy is what
   * makes that answer worth anything: the API returns `upload`, `link` and `research`, and
   * a panel that printed those three words would be showing the learner our vocabulary. The
   * sentence for each says what they can do about it — their own material they can replace,
   * a page they can open, research they did not write and cannot correct. `chat.src.failed`
   * is about the fetch and `chat.src.unreadable` about one source inside it; a briefing that
   * looks thin beside a source that could not be read has an explanation, and that is the
   * one thing on this panel anybody can act on.
   */
  'chat.src.title': 'Sources',
  'chat.src.chip': 'Where this came from',
  'chat.src.loading': 'Reading the sources…',
  'chat.src.failed': 'The sources did not load. Tap to try again.',
  'chat.src.none': 'Nothing recorded where this came from. Anything taught before nabu kept sources says so by saying nothing.',
  'chat.src.untitled': 'Untitled document',
  'chat.src.kind.upload': 'Your own material',
  'chat.src.kind.link': 'A page you added',
  'chat.src.kind.research': 'Researched for you',
  'chat.src.unreadable': 'This one could not be read, so the briefing does not rest on it.',
  'chat.src.trimmed': 'Cites {n} sources in all.',

  'chat.chips.label': 'Things you can say',
  'chat.chip.dontKnow': 'I don’t know',
  'chat.chip.dispute': 'That mark is wrong',
  'chat.chip.more': 'Tell me more',
  'chat.chip.moreQuestions': 'Ask me more',
  'chat.word.dontKnow': 'I don’t know',
  'chat.word.dispute': 'dispute ',
  'chat.word.more': 'more details',
  'chat.word.moreQuestions': '5 more questions',

  /*   * Notifications (F-102).
   *
   * The copy is doing more work here than on any other panel, because the state a
   * learner is in is decided by three things they cannot see at once: whether this
   * browser can do notifications at all, whether they have granted permission, and
   * whether the server still has a row for this device. Every one of those has its own
   * sentence rather than a shared "something went wrong", since the action differs —
   * install the app, change a browser setting, or press the button again.
   */
  'push.title': 'Notifications on this device',
  'push.hint':
    'This is how nabu tells you a question is waiting. Turn it on once per phone, tablet or computer you want to be asked on — this is a setting for this device, not for your account.',
  'push.checking': 'Checking…',
  'push.on': 'On for this device.',
  'push.off': 'Off for this device.',
  'push.enable': 'Turn on notifications',
  'push.disable': 'Turn them off here',
  'push.working': 'One moment…',
  'push.devices.none': 'No device is registered yet.',
  'push.devices.one': 'One device registered.',
  'push.devices.many': '{count} devices registered.',
  /*
   * The state where the browser and the server disagree, said plainly.
   *
   * This browser holds a subscription and the server has no row for it — which happens
   * after a subscription expires and gets pruned, or after an account is erased. It is
   * the one state in which everything looks on and nothing arrives, so it gets its own
   * sentence and its own instruction rather than being folded into "on".
   */
  'push.stale':
    'This device thinks notifications are on, but nabu has no record of it. Turn them off and on again here.',
  /*
   * Getting onto a home screen (F-121). Written for somebody who has not decided to install
   * anything yet, so it says what they get rather than what to press — the pressing is the
   * next line and is different on every platform.
   */
  'install.title': 'Put nabu on your home screen',
  /*
   * Two reasons, because there are two truths and saying the iPhone one on Android is a lie.
   *
   * Only iOS and iPadOS require an install before we can notify anybody: WebKit hides
   * `PushManager` in a tab. Android and every desktop browser can be notified without
   * installing anything, so there the install is a convenience and must be offered as one —
   * telling an Android visitor it is "the only way" would be inventing a reason to press a
   * button, which is the kind of thing people notice and stop believing.
   */
  'install.why': 'It opens like an app, without a browser bar in the way.',
  'install.why.ios':
    'On iPhone and iPad this is the only way we can send you a question when one is due.',
  'install.add': 'Add to home screen',
  'install.ios.share': 'Tap the Share button',
  'install.ios.add': 'Choose “Add to Home Screen”',
  'install.ios.open': 'Open in Safari to install',
  'install.ios.manual':
    'Your browser would not hand over. Open nabu.guru in Safari and then:',
  'install.copy': 'Copy the address',
  'install.copied': 'Copied',
  'push.install.title': 'Add nabu to your home screen first',
  'push.install.body':
    'On iPhone and iPad, notifications only work once the app is on your home screen. Tap Share, then “Add to Home Screen”, then open nabu from there and come back to this setting.',
  'push.unsupported':
    'This browser cannot show notifications. Chrome, Safari 16.4 or later, and Firefox can.',
  'push.denied':
    'This browser is blocking notifications from nabu. Nothing here can undo that — it has to be changed in the browser’s own settings for this site, and then you can turn them on again.',
  'push.error.notConfigured':
    'Notifications are not set up on this service yet. Nothing is wrong with your device.',
  'push.error.generic': 'That did not work. Try again.',
  'push.test': 'Send me a test',
  'push.test.hint':
    'It goes out exactly the way a real question does, so if it arrives here then nabu can reach you. That is the whole point of it.',
  'push.test.sent': 'On its way. If nothing arrives in a minute, notifications are not reaching you.',
  'push.test.working': 'Sending…',
  'push.test.error': 'The test could not be sent. Try again in a moment.',
  'push.test.unreachable':
    'There is nowhere to send it yet. Turn on notifications above — nabu has no other way to reach this account.',

  // ---- The navigation shell: five sections, and the conversation above them ----
  //
  // The section names are the menu's whole vocabulary, so they are short enough to read
  // at a glance in a list and specific enough to be worth a tap. Each one is also the
  // heading of the page it opens, which is what makes the menu and the screen agree.
  'nav.sections': 'Sections',
  'nav.chat': 'Chat',
  'nav.progress': 'Progress',
  'nav.plan': 'My plan',
  /*
   * The bar of plans (F-151 step 8), which nobody sees until they have two of them.
   *
   * `tabs.label` names the bar for a screen reader and is never drawn: the tabs themselves are
   * the plans' own names, so a visible heading above them would be a row of chrome saying what
   * the row below it plainly shows. `tabs.plan` is the label a plan wears for the minute
   * between being described and being named, numbered so that two unnamed plans are not two
   * identical tabs.
   */
  'tabs.label': 'Your plans',
  'tabs.plan': 'Plan',
  'tabs.unread': 'unread',
  /*
   * Not a menu entry any more, and kept because it is the right heading (F-141).
   *
   * The menu had two rows for one objective and now has one; *Research and data* is the name of
   * a section inside *My plan* rather than a destination beside it. The key keeps its `nav.`
   * prefix on purpose: these are the words a learner already knows this by, and renaming the key
   * to match its new home would change nothing they can see while making the two harder to
   * connect for anybody reading the diff that moved it.
   */
  'nav.data': 'Research and data',
  'nav.config': 'Configuration',
  'nav.profile': 'Profile',

  'plan.objective.title': 'Your objective',
  'plan.step.title': 'What happens next',
  'plan.close.title': 'Finish with this objective',
  'plan.none.title': 'There is no objective yet',
  'plan.none.body':
    'This section works on whatever you are studying, and you have not said what that is yet. Say it on your plan and everything here fills in.',
  'plan.none.action': 'Go to my plan',

  'cfg.target.title': 'Your target',

  'profile.account.title': 'Your account',
  /*
   * It used to say the account signed in and received its questions on a WhatsApp number.
   * Both halves stopped being true with F-103: the way in is a code mailed to an address, and
   * questions arrive in the chat with a notification. The address itself is now shown above
   * this line (F-140) rather than left out to keep the page free, and the section below is
   * where it is added or changed.
   */
  'profile.account.body':
    'You sign in with a six-digit code sent to your email address, so there is no password to keep. Questions arrive in the chat, and notifications are what tell you one is waiting.',

  'prog.idle':
    'Nothing to measure yet. This fills in once an objective is running and the first questions have been answered.',

  /*
   * The first run, which happens inside the wait rather than after it (F-141).
   *
   * **`setup.window` and `setup.window.do` were here and are gone with `SetupNotice`.** F-140
   * wrote them for the sequence Leo described — state an objective, wait through research,
   * approve a plan, then find nothing arrives because the one screen nobody visited was the
   * schedule — and they were a banner apologising for a state the product allowed. The state is
   * no longer allowed: `firstRun` in `onboarding/gate.ts` returns `configure` for exactly the
   * condition those two strings were shown under, and a learner in it is on this screen rather
   * than reading past a strip at the top of a different one.
   */
  'welcome.title': 'One thing first',
  'welcome.lede':
    'nabu is reading up on your objective right now. That takes five to fifteen minutes and there is nothing to watch, so let us spend it on the one setting the rest of this depends on.',
  'welcome.why':
    'Until you say when nabu may reach you, nothing it builds can be delivered — no briefings, no questions, no notifications. It is the only setting the product cannot work without, which is why it is asked for here rather than left on a screen you would have no reason to open.',
  'welcome.deciding': 'One moment…',
  'welcome.done.title': 'You are reachable',
  'welcome.done.body':
    'That was the part that had to be decided. Research is still running and your plan will be waiting when it finishes. Before that, here is what will actually arrive.',
  'welcome.done.go': 'Show me what arrives',

  'tour.title': 'What will arrive',
  'tour.lede':
    'Two examples, so you know what you are looking at when the real ones land. Nothing below was sent to you and nothing below is in your history — your first briefing is still being written.',
  /* On every sample bubble, in words, because the styling alone is not a claim a screen reader
     can hear and not a claim anybody should have to infer. */
  'tour.tag': 'Example',
  'tour.sample.briefing':
    '📖 {subject}\n\nThis is the shape a briefing arrives in: a few hundred words on one subject, written from what nabu actually read about it — the fact, the reason behind it, and the part people usually get wrong. You read it. Nothing is expected back.',
  'tour.sample.question':
    '❓ {second}: name the one thing you would want to be able to say about this from memory.\n\nThis is the shape a question arrives in. A real one is answered in your own words, in the box at the bottom of the chat, and what is sent back decides when it comes round again — sooner if it was hard, later if it was not.',
  /* Load-bearing rather than reassuring, and the reason the sample copy above is descriptive
     instead of instructing: a fake question a learner answers is graded against nothing, which
     the design names as a worse first experience than no sample at all. */
  'tour.note':
    'These two are illustrations and cannot be answered — there is nothing behind them to mark. Nothing is waiting on you: the first real question arrives on its own, with a notification, once your plan is built.',
  /* When the outline has not landed yet — it takes about forty seconds — and the objective has no
     usable title either. Still true, still theirs, and never a stranger's subject. */
  'tour.subject.fallback': 'what you are learning',
  'tour.done': 'Got it',

  'landing.deciding': 'One moment…',
  /* When the two reads behind the landing decision have failed and stopped retrying. It names the
     likely cause rather than blaming the learner, and the retry beside it is the only way out
     that does not involve reloading an installed app. */
  'landing.failed': 'nabu could not reach its own server just now.',
  'landing.retry': 'Try again',
  'update.ready': 'A new version of nabu is ready.',
  'update.reload': 'Refresh',
  'footer.built': 'Built in Costa Rica.',
} as const;

export type CopyKey = keyof typeof en;

const es: Record<CopyKey, string> = {
  'lang.name': 'Español',
  'lang.switch': 'Cambiar idioma',

  'nav.home': 'Inicio',

  'home.tagline': 'Apréndelo porque te lo preguntan.',
  'home.lede':
    'Dile a nabu qué quieres memorizar, agrega los libros y las páginas con las que ya estudias, y di para cuándo lo necesitas. Arma un plan y te enseña en la app: pregunta más sobre lo que se te sigue olvidando y menos sobre lo que ya sabes.',

  'home.how.title': 'Cómo funciona',
  'home.how.1.title': 'Di qué quieres aprender',
  'home.how.1.body':
    'Con tus propias palabras. Agrega PDF, fotos de páginas de tus libros o enlaces a lo que ya estudias.',
  'home.how.2.title': 'Revisa el plan',
  'home.how.2.body':
    'nabu investiga el tema, lee tu material y propone un temario. Vos lo reordenás, le das peso y pedís más donde se vea flojo.',
  'home.how.3.title': 'Respondé en la app',
  'home.how.3.body':
    'Las preguntas llegan cuando estás disponible y justo antes de que se te olvide. Lo que fallás vuelve pronto; lo que sabés vuelve más tarde.',

  'home.why.title': 'Por qué pregunta cuando pregunta',
  'home.why.body':
    'Cada respuesta actualiza un modelo de lo que recordás, y de ahí sale el horario — no de un calendario fijo. Si decís que tenés examen una fecha, los intervalos se comprimen hacia ella. Si decís que querés mantener un 85%, se acomoda a eso.',

  'home.cta.title': 'En construcción',
  'home.cta.body':
    'Entrar ya funciona. Definir un objetivo y recibir un temario son lo siguiente — así que todavía no hay nada que hacer adentro, pero ya podés tener cuenta.',
  'home.cta.button': 'Entrar',

  'signin.title': 'Entrar',
  'signin.code.label': 'El código de seis dígitos',
  'signin.code.hint': 'Vence en diez minutos.',
  'signin.code.submit': 'Entrar',
  'signin.working': 'Un momento…',
  'signin.error.code': 'Ese código está mal o ya venció. Pedí otro.',
  'signin.error.session':
    'Tu código estaba bien, pero la sesión no volvió: puede que estés sin conexión, o que este navegador esté bloqueando las cookies de nabu.guru. Revisá las dos cosas y volvé a entrar.',
  'signin.error.tooMany': 'Se pidieron demasiados códigos. Esperá un rato y volvé a intentar.',
  'signin.error.generic': 'Algo salió mal. Intentá de nuevo.',

  'signin.email.label': 'Tu correo electrónico',
  'signin.email.hint':
    'El código llega por correo. Lo único más que mandamos ahí es un aviso cuando hay una ' +
    'pregunta esperando y las notificaciones no te alcanzan, y eso lo podés cancelar cuando ' +
    'querás.',
  'signin.email.back': 'Cambiar correo',
  'signin.error.email': 'Eso no parece un correo electrónico.',
  'signin.error.unconfigured':
    'Ahora mismo no se pueden mandar códigos. Es algo nuestro, no tuyo — probá de nuevo en un rato.',

  'email.title': 'Entrar por correo',
  'email.body':
    'Agregá un correo y queda como una segunda forma de entrar a esta misma cuenta — los mismos objetivos, el mismo plan, la misma historia. Ahí no se manda nada más que el código para entrar.',
  'email.label': 'Correo electrónico',
  'email.submit': 'Enviarme un código',
  'email.code.label': 'El código de seis dígitos',
  'email.code.hint': 'Vence en diez minutos.',
  'email.confirm': 'Agregar este correo',
  'email.cancel': 'Cancelar',
  'email.done': 'Listo. Ya podés entrar con {email}.',
  'email.error.email': 'Eso no parece un correo electrónico.',
  'email.error.code': 'Ese código está mal o ya venció. Pedí otro.',
  'email.error.inUse': 'Ese correo ya entra a otra cuenta.',
  'email.error.tooMany': 'Se pidieron demasiados códigos. Esperá un rato y volvé a intentar.',
  'email.error.generic': 'Algo salió mal. Intentá de nuevo.',

  'mail.title': 'Correo a esta dirección',
  'mail.body':
    'Los códigos para entrar siempre llegan por correo. También un aviso cuando hay una ' +
    'pregunta esperando y las notificaciones no te alcanzan. Lo demás lo apagás por ' +
    'separado, acá o desde cualquier mensaje que te mandemos.',
  'mail.checking': 'Revisando…',
  'mail.bouncing.hard':
    'El correo a {email} está siendo rechazado, así que dejamos de enviarle. Incluidos los ' +
    'códigos para entrar: un código a un buzón que lo rechaza no le llega a nadie.',
  'mail.bouncing.complaint':
    'El correo a {email} fue reportado como spam, así que dejamos de enviarle. Incluidos ' +
    'los códigos para entrar.',
  'mail.bouncing.diagnostic': 'Lo que dijo el servidor de correo: {diagnostic}',
  'mail.retry': 'Ya lo arreglé — probá de nuevo',
  'mail.retry.working': 'Probando…',
  'mail.retry.done': 'Listo. Pedí un código para entrar y mirá si llega.',
  'mail.retry.tooSoon': 'Eso se probó hace muy poco. Esperá una hora y volvé a intentar.',
  'mail.retry.notSuppressed': 'Esta dirección no tiene ningún problema.',
  'mail.retry.noAddress': 'Esta cuenta todavía no tiene una dirección.',
  'mail.nudge.label': 'Un aviso cuando hay una pregunta esperando y no podemos notificarte',
  'mail.nudge.on': 'Enviármelo',
  'mail.nudge.off': 'No enviármelo',
  'mail.nudge.hint':
    'Cuando hay una pregunta para vos y no hay ningún dispositivo al que podamos mandarte ' +
    'una notificación, te mandamos un correo corto: qué te está esperando y un enlace, ' +
    'nunca la pregunta. Los códigos para entrar no se ven afectados de ninguna manera.',
  'mail.nudge.onlyChannel':
    'Ahora mismo no hay ningún dispositivo al que podamos avisarte, así que este correo es ' +
    'la única forma que tiene nabu de llegarte. Si lo apagás, no te va a llegar nada hasta ' +
    'que abras la app vos mismo.',
  'mail.nudge.unreachable':
    'Esto está apagado y no hay ningún dispositivo al que podamos avisarte, así que no te ' +
    'va a llegar nada: las preguntas quedan esperando en la conversación hasta que abras ' +
    'nabu. Está bien si es lo que querés — podés volver a activarlo acá cuando sea.',
  'mail.nudge.unreachableBouncing':
    'No hay ningún dispositivo al que podamos avisarte y el correo a esta dirección está ' +
    'siendo rechazado, así que no te puede llegar nada hasta que el buzón vuelva a ' +
    'funcionar, digan lo que digan estos interruptores.',
  'mail.reports.label': 'Resúmenes de cómo va tu estudio',
  'mail.reports.on': 'Enviármelos',
  'mail.reports.off': 'No enviármelos',
  'mail.reports.hint':
    'Resúmenes al final del día y cualquier otra cosa que escribamos sobre tu avance. ' +
    'Todavía no mandamos ninguno; este es el interruptor que los va a parar cuando los ' +
    'mandemos. No tiene nada que ver con el aviso de arriba.',
  'mail.switches.unsubscribe':
    'El enlace para cancelar que viene en cualquier correo nuestro apaga los dos a la vez: ' +
    'es un solo botón en tu aplicación de correo y no puede preguntarte a cuál te referís. ' +
    'Los códigos para entrar nunca se ven afectados, y acá podés volver a activar el que ' +
    'quieras.',
  'mail.switches.suppressed':
    'Mientras el correo a esta dirección esté siendo rechazado, no se manda nada, digan lo ' +
    'que digan estos interruptores.',
  'mail.error.generic': 'No se pudo leer esto ahora. Intentá de nuevo.',

  'unsub.title': 'Cancelar estos correos',
  'unsub.working': 'Guardando…',
  'unsub.done':
    'Listo. Cancelamos todos los correos menos los códigos para entrar: nadie puede ' +
    'cancelar su propia forma de entrar. Eso incluye el aviso que mandamos cuando hay una ' +
    'pregunta esperando y las notificaciones no te alcanzan, así que si las notificaciones ' +
    'no funcionan, no te va a llegar nada hasta que abras la app. Si solo querías cancelar ' +
    'un tipo, cada uno tiene su propio interruptor en tu cuenta.',
  'unsub.noLink':
    'Esta página necesita el enlace del correo. Abrilo desde el mensaje, o cambiá la ' +
    'preferencia desde tu cuenta.',
  'unsub.home': 'Ir a nabu',

  /*
   * A sentence rather than a label: this is the line shown where `session.as` would go when
   * the account has no address on file, so "Dentro" on its own would read as a fragment.
   */
  'session.signedIn': 'Estás dentro',
  'session.as': 'Estás dentro con {email}',
  'session.signOut': 'Salir',
  'session.objectives': 'Mis objetivos',
  'session.chat': 'Conversación',

  'obj.new.title': '¿Qué querés aprender?',
  'obj.description.label': 'Describilo con tus propias palabras',
  'obj.description.placeholder':
    'Por ejemplo: memorizar los puntos más importantes de todos los meridianos en MTC, incluyendo los cinco puntos de los elementos, fuente, conexión, hendidura, mu, shu, entrada y salida — el número, nombre, ubicación y descripción de cada uno.',
  'obj.description.hint':
    'Escribí todo lo que querás. Este texto exacto es lo que se investiga, así que el detalle ayuda.',
  'plan.source.label': '¿De dónde sale el material?',
  'plan.source.research': 'Buscalo por mí',
  'plan.source.research.hint':
    'nabu investiga el tema en la web y arma el plan con lo que encuentra. También podés agregar tus propias páginas cuando quieras.',
  'plan.source.uploads': 'Ya lo tengo',
  'plan.source.uploads.hint':
    'El plan se arma solo con las páginas que agregues — sacá fotos de las páginas del libro, o subí un PDF. No se busca nada en la web.',
  'plan.source.scoped': 'Tengo una parte',
  'plan.source.scoped.hint':
    'El plan se arma alrededor de las páginas que agregues, y nabu investiga lo que cubren y lo que dan por sabido. Si agregás más páginas después, vuelve a mirar.',
  'obj.new.submit': 'Guardar',
  'obj.working': 'Trabajando…',
  'obj.target.label': '¿Cómo vas a saber que ya está?',
  'obj.target.score': 'Un porcentaje que quiero mantener',
  'obj.target.date': 'Una fecha para la que lo necesito',
  'obj.target.scoreHint': 'con qué frecuencia querés acertarlos',
  'obj.target.dateHint': 'los intervalos se comprimen al acercarse la fecha',
  'obj.list.title': 'Tus objetivos',
  'obj.list.loading': 'Cargando…',
  'obj.list.empty': 'Todavía ninguno.',
  'obj.status.drafting': 'Borrador',
  'obj.status.researching': 'Investigando',
  'obj.status.synthesising': 'Armando el plan',
  'obj.status.review': 'Listo para revisar',
  'obj.status.active': 'En marcha',
  'obj.status.done': 'Terminado',
  'obj.status.abandoned': 'Abandonado',
  'obj.status.shelved': 'De lado',
  'obj.current.description': 'Lo que pediste',
  'obj.target.save': 'Guardar meta',
  'obj.target.saved': 'Guardado.',
  'obj.close.done': 'Ya me lo sé — darlo por terminado',
  'obj.close.abandon': 'Ya no lo voy a seguir',
  'obj.close.confirm': 'Esto cierra el objetivo y te deja empezar otro. ¿Seguro?',
  'obj.close.hint':
    'Trabajás en un objetivo a la vez. Cerrar este abre el siguiente — y nada de lo que cerrás se pierde: queda en la lista de abajo y podés volver a él.',
  'obj.next.title': 'Listo para el siguiente',
  'obj.next.body': 'Tu objetivo anterior está cerrado. Describí lo próximo que querés aprender.',

  'obj.history.title': 'Objetivos que cerraste',
  'obj.history.hint':
    'Ahora no hay ninguno en marcha. Describí algo nuevo arriba, o retomá uno de estos donde lo dejaste.',
  'obj.history.switchHint':
    'Tenés uno en marcha. Si traés otro de vuelta, el de ahora queda de lado — conserva todo lo que tiene, cae en esta lista y se retoma igual.',
  'obj.history.closed': 'cerrado el',
  'obj.history.reactivate': 'Retomar este',
  'obj.history.confirm':
    'Esto deja de lado el objetivo en el que estás y pone este en su lugar. No lo estás abandonando: cae en esta misma lista con todo lo que tiene, y lo podés retomar cuando querás. ¿Le damos?',
  'obj.history.untitled': 'Objetivo sin título',
  'obj.history.switched': 'Listo. El que tenías en marcha quedó de lado en esta lista.',
  'obj.history.switchedMidRun':
    'Listo. El que tenías en marcha quedó de lado en esta lista — su investigación sigue corriendo, termina sola, y te avisamos cuando esté.',
  'obj.history.switchedMidBuild':
    'Listo. El que tenías en marcha quedó de lado en esta lista — su plan se sigue armando, termina solo, y el resultado te queda esperando ahí.',
  'obj.history.resumed': 'De vuelta en este.',

  'src.title': 'Tu propio material',
  'src.hint':
    'Opcional. Sacale una foto a una página de tu libro, o agregá un PDF, y se lee para usarlo junto con la investigación.',
  'src.hint.wanted':
    'A tu plan le faltan unas cosas que pediste. Una foto de una página que cubra alguna de ellas, o un PDF, es justo lo que cierra el hueco.',
  'src.choose': 'Agregar páginas o un PDF',
  'src.working': 'Leyéndolo…',
  'src.automatic':
    'Todo lo que agregues acá se suma al plan solo, y las preguntas de eso se escriben enseguida. No hace falta que aprietes nada más.',
  'src.empty': 'Nada agregado.',
  'src.show': 'Ver lo que se leyó',
  'src.hide': 'Ocultar',
  'src.loading': 'Cargando…',
  'src.characters': 'caracteres',
  'src.truncated': 'recortado para mostrarlo',
  'src.status.pending': 'Leyendo',
  'src.status.extracted': 'Leído',
  'src.status.failed': 'No se pudo leer',
  'src.error.type': 'Ese tipo de archivo no sirve. Solo fotos y PDF.',
  'src.error.camera':
    'Esa es una foto de iPhone en HEIC y todavía no se puede leer. En Ajustes → Cámara → Formatos, elegí Más compatible, o compartí la foto como JPEG antes de subirla.',
  'src.error.tooLarge': 'Ese archivo es muy grande. Las fotos llegan hasta',
  'src.error.upload': 'La subida no se completó. Probá de nuevo.',
  'src.error.generic': 'Algo salió mal con ese archivo.',
  'src.failed.no_text': 'No se encontró texto en esta. Probá una foto más derecha y con más luz.',
  'src.failed.too_large':
    'Este archivo es muy grande para leerlo. Volvé a tomarlo en un tamaño menor.',
  'src.failed.unsupported_format': 'Este archivo no está en un formato que se pueda leer.',
  'src.failed.too_many_pages':
    'Este PDF tiene demasiadas páginas. Dividilo y agregá la parte que estás estudiando.',
  'src.failed.truncated':
    'Una página de esto tiene más texto del que se puede leer de una sola vez. Sacale una foto solo a esa página y suele salir.',
  'src.retry': 'Intentar leerlo de nuevo',
  'src.retrying': 'Intentando de nuevo…',
  'src.remove': 'Quitar',
  'src.removing': 'Quitando…',
  'src.remove.confirm':
    '¿Quitar esto de tu material? Lo que se haya leído de ahí también se va. Tu plan se queda con lo que ya aprendió de eso hasta que lo reconstruyas.',
  'src.action.failed': 'Eso no funcionó. Probá de nuevo.',
  'src.failed.internal': 'Algo salió mal al leer esta. No fue tu archivo.',
  'obj.error.target': 'Ese objetivo no sirve — la fecha tiene que ser futura.',
  'obj.error.description': 'Contá algo de lo que querés aprender.',
  'obj.error.signIn': 'Entrá primero.',
  'obj.error.generic': 'Algo salió mal. Intentá de nuevo.',
  'obj.error.alreadyActive':
    'Ya tenés tantos objetivos en marcha como permite tu plan. Cerrá o archivá uno para empezar otro.',
  'obj.error.notFound': 'Ese objetivo ya no está. Se recargó la lista.',
  'obj.error.notClosed': 'Ese no está cerrado. Se recargó la lista — mirá de nuevo.',
  'obj.error.conflict': 'Algo se movió mientras mirabas esa lista. Se recargó — mirá de nuevo.',

  'cur.rebuild': 'Armar el plan de nuevo',
  'cur.rebuild.body':
    '¿No quedó bien? Armarlo de nuevo reemplaza este plan usando la misma investigación — toma un par de minutos y no repite el paso caro.',
  'session.checking': 'Un momento…',
  'win.pause.label': 'Mandame preguntas',
  'win.pause.running': 'Se te están mandando preguntas dentro de las horas de abajo.',
  'win.pause.stopped':
    'Detenido. No se está mandando nada, y no se pierde nada — tu plan y tu progreso quedan igual. Volvelo a activar cuando querás.',
  'win.pause.stoppedBySilence':
    'Se detuvo solo después de unos recordatorios sin respuesta. No se perdió nada — activalo de nuevo, o mandá “seguir” en el chat.',
  'win.pause.failed': 'Eso no se guardó. Probá de nuevo.',
  'start.title': 'Empezar a aprender',
  'start.body':
    'Tus preguntas están listas. Al empezar, te van llegando dentro de las horas que pusiste — en el chat de acá, con una notificación cuando hay una esperando — y nada llega fuera de ellas.',
  'start.button': 'Empezar a mandarme preguntas',
  'start.confirm':
    'Esto empieza a mandarte preguntas dentro de las horas que pusiste. Lo podés pausar cuando querás. ¿Le damos?',
  'start.working': 'Empezando…',
  // Voseo: `pusiste`, and the hours are the ones the learner set.
  'start.running': 'En marcha — las preguntas te llegan dentro de las horas que pusiste.',
  'start.error.nothingActive':
    'Están todas las materias destildadas, así que no hay nada que mandar. Tildá al menos una arriba.',
  'start.error.noItems': 'Todavía no hay preguntas. Escribilas primero, arriba.',
  'gap.title': 'Lo que solo vos podés agregar',
  'gap.body':
    'Esto lo pediste y la investigación no lo encontró, así que no está en tu plan y no se te va a preguntar. Una foto de una página de tu propio libro es justo lo que lo agrega.',
  'gap.action': 'Subí una página que cubra esto',
  'gap.more': 'Ver el resto',
  'gap.less': 'Ver menos',
  'gap.after.review':
    'Primero agregá la página y después armá el plan de nuevo más abajo: vuelve a leer todo, incluido lo que acabás de agregar, y esta lista se acorta. Toma un par de minutos y no repite la investigación.',
  'gap.after.active':
    'Primero agregá la página y después sumala al plan acá abajo: vuelve a leer todo lo que subiste y agrega lo nuevo. No se pierde nada de lo que ya aprendiste, y las preguntas te siguen llegando todo el tiempo.',

  'fold.title': 'Sumá al plan lo que subiste',
  'fold.body':
    'Vuelve a leer todo lo que agregaste y suma al plan lo que sea nuevo. Los temas que venís estudiando quedan en su lugar, y todo lo que ya te aprendiste de ellos se mantiene — el plan solo crece. Las preguntas te siguen llegando mientras tanto.',
  'fold.button': 'Agregar al plan lo que subí',
  'fold.working': 'Sumándolo…',
  'fold.running':
    'Está leyendo todo de nuevo y sumando lo nuevo. Un minuto o dos, y las preguntas de eso se escriben después. Podés cerrar la página: sigue sin vos, y tus preguntas no se detienen.',
  'fold.error.running': 'Eso ya está pasando: arrancó hace un momento y toma un minuto o dos.',
  'fold.error.notReady':
    'Este objetivo cambió desde la última vez que esta página lo miró. La página ya se actualizó.',
  'fold.failed':
    'El último intento de sumar tu material no terminó. Tu plan y todo lo que aprendiste quedaron igual: no se cambió nada. Qué pasó:',
  'fold.writing':
    'Tu plan ya tiene el material nuevo. Ahora está escribiendo las preguntas de eso: unos minutos, y solo de lo que cambió. Podés cerrar la página; sigue sin vos.',
  'fold.outcome':
    '{topics} temas nuevos, {questions} preguntas nuevas. Te van a empezar a llegar junto con las de siempre.',
  'fold.reworded':
    '{topics} temas reescritos, {questions} preguntas nuevas. Te van a empezar a llegar junto con las de siempre.',
  'fold.nothingNew':
    'Todo lo que agregaste ya estaba en tu plan: no había nada nuevo para sumar, y no hizo falta ninguna pregunta nueva.',

  'again.title': '¿Falta algo en tu plan?',
  'again.body':
    'nabu vuelve a la web a buscar temas que tu plan todavía no cubre. Lo que encuentre se suma al material que ya tenés: tu plan, tus preguntas y todo lo que llevás aprendido quedan exactamente igual hasta que vos mismo sumes lo nuevo al plan, más abajo.',
  'again.honest':
    'Le decimos qué cubre tu plan hoy y le pedimos solo lo que no está, así que no te va a devolver el mismo material. Si no falta nada, lo dice y para. Para tener más sobre un tema que ya está, usá el botón de ese tema.',
  'again.note.label': '¿Qué falta? (opcional)',
  'again.note.placeholder': 'ej. las tablas de puntos de los Cinco Shu',
  'again.button': 'Buscar lo que falta',
  'again.working': 'Arrancando…',
  'again.running':
    'Buscando lo que falta. Un minuto o dos para ver si falta algo, y después cada tema nuevo se investiga en segundo plano. Podés cerrar la página: sigue sin vos, te avisamos, y mientras tanto no cambia nada de tu plan ni de tus preguntas.',
  'again.locked':
    'Este botón vuelve media hora después de arrancar una búsqueda, haya terminado o no. Es a propósito, en lo más pesado de acá: nada lo puede arrancar dos veces sin querer.',
  'again.confirm':
    'Esto arranca una investigación nueva. Se suma al material que ya tenés y, por sí solo, no cambia nada de tu plan ni de tu progreso. ¿Le damos?',
  'again.error.running':
    'Ya se está investigando algo en este objetivo. Dale un minuto — o una hora, si ya va por los temas.',
  'again.error.notReady':
    'Este objetivo no está en un punto donde se pueda arrancar otra investigación. La página ya se actualizó.',
  'again.next': 'Cuando llegue, sumalo al plan acá abajo. Hasta que lo hagás, no cambia nada.',

  'del.title': 'Borrar todo',
  'del.body':
    'Esto borra tu cuenta y todo lo que hay en ella: cada objetivo, los planes que se armaron, cada pregunta escrita para vos, todo lo que llevás aprendido, todo tu historial de mensajes, las páginas que fotografiaste y el texto que se leyó de ellas, y el vínculo entre tu correo y esta cuenta. Los mensajes se detienen en el momento en que corre.',
  'del.irreversible':
    'No hay deshacer y no hay copia. No se archiva nada, no se recupera nada pidiéndolo, y si volvés a entrar con el mismo número empezás desde cero.',
  'del.kept':
    'Se guarda una sola cosa: el conteo mensual de lo que nabu gastó en llamadas al modelo para esta cuenta. No tiene texto — nada de lo que escribiste, nada de lo que subiste, solo números, bajo un id que ya no es de nadie — y es contra lo que se revisa la factura.',
  'del.ack': 'Entiendo que esto no se puede deshacer.',
  'del.button': 'Borrar mi cuenta y todo lo que hay en ella',
  'del.working': 'Borrando…',
  'del.confirm':
    'Última revisión. Esto borra tus objetivos, tus planes, tus preguntas, todo lo que llevás aprendido, tus mensajes y las páginas que subiste. No se puede deshacer. ¿Borramos todo?',
  'del.done.title': 'Listo, ya no está',
  'del.done.body': 'Tu cuenta y todo lo que había en ella se borraron, y ya saliste de la sesión.',
  'del.done.counts':
    '{objectives} objetivos, {nodes} temas, {items} preguntas, {reviews} cosas que llevabas aprendidas, {messages} mensajes y {objectVersions} archivos guardados — contando cada versión anterior de ellos.',
  'del.done.home': 'Volver al inicio',
  'del.failed':
    'El borrado no terminó. Parte de tus datos ya no está y parte puede seguir ahí: esto no se detuvo limpio. Probá de nuevo, que sigue desde donde se quedó. Si vuelve a fallar, decilo en vez de dar por hecho que funcionó.',
  'del.error.notFound': 'Acá no hay cuenta que borrar. Puede que ya la hayas borrado.',
  'del.error.notConfirmed': 'Eso no llegó confirmado. Marcá la casilla y probá de nuevo.',

  'res.title': 'Lo que encontró la investigación',
  'res.hint':
    'Este es el texto exacto con el que se armó tu plan. Al final está lo que no se pudo encontrar — vale la pena verlo antes de armarlo.',
  'res.characters': 'caracteres',
  'res.truncated': 'cortado aquí; se usó todo para armar el plan',

  // F-141 — ver el bloque en inglés.
  'res.empty.none':
    'Todavía no se leyó nada sobre esto. Cuando arranque la investigación, cada tema que se mire va apareciendo acá.',
  'res.empty.uploads':
    'Este plan se arma con las páginas que vos agregás, así que no se está buscando nada en la web. Lo que agregues aparece abajo, en tu material.',

  // F-109 — ver el bloque en inglés.
  'res.progress.title': 'Investigación en curso',
  'res.progress.outlining': 'Viendo de qué está hecho el tema',
  'res.progress.deepening': 'Investigando cada tema, uno por uno',
  'res.progress.done': 'Investigación terminada',
  'res.progress.failed': 'La investigación se detuvo',
  'res.progress.of': '{done} de {total} temas investigados',
  'res.progress.slow':
    'Cada tema se investiga en segundo plano, y eso puede tardar hasta una hora. Podés cerrar esta página — te avisamos cuando esté.',
  'res.state.outlined': 'en espera',
  'res.state.queued': 'en cola',
  'res.state.researching': 'leyendo',
  'res.state.deep': 'investigado',
  'res.state.thin': 'poco encontrado',
  'res.state.barren': 'sin resultados',
  'res.state.unfunded': 'fuera del plan',
  'res.state.failed': 'falló',
  'res.missing': 'No se encontró:',
  'res.deeper': 'Profundizar',
  'res.deeper.working': 'Arrancando…',
  'res.deeper.confirm':
    'Esto investiga este tema con más profundidad. Se suma a tu material y no cambia nada de tu plan ni de tu progreso. ¿Le damos?',
  'res.error.running': 'Ya se está investigando algo en este objetivo. Dale un minuto.',
  'res.error.notReady': 'Este objetivo no está en un punto donde se pueda investigar más.',
  'res.error.noSubject': 'Ese tema ya no está. Recargá la página.',
  'res.error.generic': 'Eso no arrancó. Probá de nuevo en un momento.',
  'res.attempts': 'intento {attempts}',

  // F-144, F-147 y F-149. Ver el bloque en inglés: una espera de dieciséis minutos que dice qué
  // está haciendo, con los nombres de los cinco grupos en las palabras de quien aprende.
  'pulse.starting.body':
    'Arrancó. nabu está averiguando de qué está hecho tu objetivo — los primeros temas aparecen acá en un minuto o dos, y después se van completando de a uno.',
  'pulse.reading': 'leyendo ahora',
  'pulse.waiting': 'por venir',
  'pulse.found': 'investigados',
  'pulse.empty': 'sin resultados',
  'pulse.stalled': 'sin alcanzar',
  'pulse.chart.caption': '{characters} caracteres de material, en {minutes} minutos',
  'pulse.chart.alt':
    'Cómo fue llegando el material durante la pasada: {characters} caracteres en {minutes} minutos.',
  'pulse.quote.from': 'De {subject}',
  'pulse.findings': '{found} de {subjects} temas investigados · {characters} caracteres leídos',
  'pulse.findings.gaps': '{gaps} de ellos dijeron qué no pudieron encontrar.',
  'pulse.seeAll': 'Ver todo lo que se leyó',

  // F-141. Ver el bloque en inglés: el carrusel que gira mientras se investiga, y sus cuatro
  // clases de tarjeta — lo que el esquema ya escribió, lo que se acaba de encontrar, y lo que
  // no se encontró.
  'carousel.roledescription': 'carrusel',
  'carousel.label': 'Lo que se está leyendo, de a una tarjeta',
  'carousel.kind.outline': 'En tu plan',
  'carousel.kind.promise': 'Lo que vas a aprender',
  'carousel.kind.finding': 'Recién encontrado',
  'carousel.kind.gap': 'Todavía buscando',
  'carousel.from': '{title} · tema {place} de {subjects}',
  'carousel.read': '{characters} caracteres leídos',
  'carousel.prev': 'Tarjeta anterior',
  'carousel.next': 'Tarjeta siguiente',
  'carousel.pause': 'Pausar',
  'carousel.resume': 'Volver a arrancar',
  'carousel.position': '{place} de {total}',

  // F-129. Ver el bloque en inglés: lo que pasó con cada tema, y cuáles llegaron al plan.
  'cov.summary': '{researched} temas investigados · {inPlan} en tu plan',
  'cov.summary.missing': '{missing} no llegaron al plan. Cada uno dice por qué.',
  'cov.summary.pending': '{pending} todavía se están leyendo.',
  'cov.summary.unknown':
    'Este plan se armó antes de que nabu guardara de qué tema salía cada punto, así que acá las dos listas no se pueden cruzar. El próximo plan que arme sí.',
  'cov.outcome.deep': 'investigado',
  'cov.outcome.thin': 'poco encontrado',
  'cov.outcome.barren': 'no se encontró nada',
  'cov.inPlan.one': '1 tema en tu plan',
  'cov.inPlan.many': '{nodes} temas en tu plan',
  'cov.notInPlan': 'No está en tu plan',
  'cov.why.deep':
    'Se investigó, y al armar el plan no se usó. Subir una página tuya, o volver a buscar, es lo que cambia eso.',
  'cov.why.thin':
    'Se encontró algo y no alcanza a cubrir el tema, así que no había con qué enseñarlo.',
  'cov.why.barren':
    'No volvió nada aprovechable sobre esto. Hay temas de los que casi no hay nada escrito en ninguna parte, y eso es un dato del tema, no una falla nuestra.',
  'cov.remedy.upload': 'Subir una página tuya',
  'cov.remedy.again': 'Buscar otra vez este tema',
  'cov.missing.title': 'Lo que no llegó a tu plan',
  'cov.missing.body':
    'Cada uno de estos se investigó y no quedó en el plan. Debajo de cada uno está lo que le faltó, en las palabras de la pasada que lo buscó.',
  'cov.thin.title': 'Un plan corto, y por qué es corto',
  'cov.thin.body':
    'Tu plan cubre {inPlan} de los {researched} temas investigados. Eso fue lo que hubo para armarlo, y los temas que sí cubre valen la pena. Subir una página de un libro tuyo, o pedirle a nabu que busque otra vez un tema, es lo que puede cambiar el resto.',
  'cov.thin.action': 'Ver todos los temas',
  'cov.unattributed':
    'Este plan se armó antes de que nabu guardara de qué tema salía cada punto, así que acá no te puede mostrar qué temas hay detrás. Al plan mismo no le falta nada.',
  'cov.from': 'De {subject}',
  'cov.more': 'Ver el resto',
  'cov.less': 'Ver menos',

  'items.title': 'Tus preguntas',
  'items.build.body':
    'El plan de arriba es sobre lo que te vamos a preguntar. Escribirlas pasa en el fondo y toma más entre más grande sea el plan — podés cerrar esto y volver después.',
  'items.build': 'Escribir mis preguntas',
  'items.working': 'Escribiéndolas…',
  'items.ready': 'Listas',
  'items.rebuild': 'Escribirlas de nuevo',
  'items.failed.body': 'Las preguntas no se escribieron.',
  'items.writing': 'Escribiéndolas',
  // Voseo, like the rest of this dictionary: `cerrás`, not `cierras`.
  'items.writing.body':
    'Tus preguntas se están escribiendo ahora mismo — no hay nada más que apretar. Un plan grande toma su rato, pasa en el fondo, y no se pierde nada si cerrás esto. Aparecen acá cuando estén.',
  'plan.error.stillResearching':
    'Todavía estamos investigando — el temario se arma solo a medida que llega, y te avisamos cuando esté.',
  'items.error.notReady':
    'Todavía no hay plan sobre el que escribir preguntas. Armá primero el de arriba.',
  'items.error.alreadyRunning':
    'Ya se están escribiendo para este plan. Dale un rato — esta página te avisa cuando estén.',
  'items.folding':
    'Tu plan se está actualizando ahora mismo. Las preguntas se pueden escribir apenas termine.',

  'obj.error.notDrafting': 'La investigación de este ya estaba arrancada. Se actualizó la página.',
  'obj.error.notReady': 'Este objetivo todavía no está listo para un temario.',
  'obj.error.noMaterial':
    'Todavía no hay con qué armar el temario — no llegó ninguna investigación y no se leyó nada de lo que agregaste.',

  'obj.material.title': 'Agregá tu material',
  'obj.material.body':
    'Este plan se arma con lo que agregues — sacá fotos de las páginas del libro, o subí un PDF. Con un par de páginas alcanza para arrancar, y el plan aparece acá solo una vez que se hayan leído. No se está buscando nada en la web.',
  'obj.material.button': 'Agregar páginas',
  'obj.material.switch':
    '¿Cambiaste de idea? Podés pedirle a nabu que investigue el tema, en Configuración.',
  'obj.material.body.scoped':
    'Este plan se arma alrededor de lo que agregues — sacá fotos de las páginas del libro, o subí un PDF. Con un par de páginas alcanza para arrancar. Apenas se lean, el plan aparece acá y nabu sale a investigar lo que cubren. Hasta entonces no se busca nada.',
  'obj.material.switch.scoped':
    '¿Cambiaste de idea? Podés pedirle a nabu que investigue todo el tema, o que use solo tus páginas, en Configuración.',
  'obj.error.noResearch':
    'Este objetivo se arma con tu propio material, así que no hay nada que investigar. Podés cambiarlo en Configuración.',
  'obj.error.awaitingMaterial':
    'Este plan se arma con tus propias páginas. Agregá una y nabu empieza a investigar solo.',
  'obj.error.busy': 'Hay algo corriendo en este objetivo ahora mismo. Probá de nuevo en un minuto.',
  'obj.error.researchGrantSpent':
    'Ya usaste la investigación de este mes. Tu plan incluye una cantidad fija de investigaciones por mes, y arranca de nuevo cuando cambia el mes.',

  'cfg.source.title': 'De dónde sale el material',
  'cfg.source.body':
    'Podés cambiar esto cuando quieras, y no se pierde nada de lo que ya tenés. Elegir que nabu busque el material no lo pone a buscar — eso se ofrece en la pantalla del plan cuando estés listo.',
  'cfg.source.save': 'Guardar esto',
  'cfg.source.saved': 'Guardado.',

  'obj.research.title': 'Investigar esto',
  'obj.research.body':
    'nabu investiga el tema en la web y anota lo que encuentra, con la fuente de cada dato. Toma entre cinco y quince minutos, y corre una sola vez — es lo más caro que hace nabu, así que arrancá cuando la descripción de arriba diga lo que querés decir.',
  'obj.research.start': 'Arrancar la investigación',
  'obj.research.starting': 'Arrancando…',
  'obj.research.running.title': 'Leyendo sobre el tema',
  'obj.research.running.body':
    'Un minuto o dos para ver de qué está hecho tu tema. Los temas van apareciendo arriba a medida que los encuentra, y después cada uno se investiga a fondo en segundo plano — eso puede tardar hasta una hora, y te avisamos cuando esté.',
  'obj.wait.slow':
    'Todavía sigue, y esta página dejó de revisar en vez de quedarse recargando toda la tarde. No se perdió nada.',
  'obj.wait.check': 'Revisar de nuevo',
  'obj.research.failed.title': 'La investigación se detuvo',
  'obj.research.failed.body':
    'No llegó a dejar nada, así que el objetivo volvió a ser un borrador. Qué pasó:',
  'obj.research.retry': 'Arrancar la investigación otra vez',

  'cur.title': 'El plan',
  'cur.build.body':
    'Ya están los hallazgos. Armar el temario convierte todo lo leído hasta ahora en un conjunto ordenado de temas — un minuto o dos.',
  'cur.build': 'Armar el temario',
  'cur.failed.body':
    'El último intento de armar el temario no terminó. Todo lo que se leyó sigue ahí, así que volver a intentarlo paga el armado y no la investigación. Qué pasó:',
  'cur.building.title': 'Armando el temario',
  'cur.building.body':
    'Una pasada sobre todo lo leído hasta ahora. Normalmente un minuto o dos, y el plan aparece aquí cuando esté.',
  'cur.empty': 'Todavía no hay plan.',
  'cur.topics': 'temas',
  'cur.weight.label': 'La banda de cada tema dice cuánto del plan vale — no con qué frecuencia te van a preguntar.',
  'cur.weight.core': 'Clave',
  'cur.weight.major': 'Importante',
  'cur.weight.minor': 'De apoyo',
  'cur.weight.aside': 'Al margen',
  'cur.subjects': 'materias',
  'cur.inside.one': 'tema dentro',
  'cur.inside.many': 'temas dentro',
  'cur.sections.openAll': 'Abrir todas las materias',
  'cur.sections.closeAll': 'Cerrar todas las materias',
  'cur.active.hint':
    'Todas las materias arrancan tildadas. Si destildás una, nabu no le escribe preguntas ni te pregunta nada de ella — el resto del plan sigue igual.',
  'cur.active.aria': 'Estudiar esta materia',
  'cur.active.off': 'Sin estudiar',
  'cur.active.failed': 'No se guardó, así que quedó como nabu lo sigue teniendo. Probá de nuevo.',
  'cur.active.none':
    'No hay nada tildado, así que a nabu no le queda nada que preguntarte. Tildá al menos una materia.',
  'cur.summaries.show': 'Ver las descripciones completas',
  'cur.summaries.hide': 'Acortar las descripciones',
  'cur.untitled': 'Tema sin título',

  'prog.title': 'En qué vas',
  'prog.loading': 'Cargando…',
  'prog.unreadable':
    'No se pudo leer tu progreso. Es una falla de acá y no un comentario sobre cómo vas — recargá la página.',
  'prog.headline': 'del plan, ponderado por cuánto vale cada materia',
  'prog.headline.none':
    'Todavía no hay nada que medir: no se han escrito preguntas para las materias que estás estudiando.',
  'prog.counts':
    '{holding} sostenidas · {struggling} por trabajar · {untested} sin preguntar todavía, de {questions} preguntas.',
  'prog.answered.none':
    'Todavía no te han preguntado nada, así que no se sabe nada de cómo vas. Es un resultado vacío, no uno malo.',
  'prog.answered':
    'Respondiste {answered} de {questions} preguntas. Sobre esas {answered}, y solo esas, nabu calcula en {recall} tu posibilidad de acertar hoy.',
  'prog.pace.score':
    'No hay fecha con la cual estar al día. Pediste sostener {target}, que es el estándar contra el que nabu programa.',
  'prog.pace.date.ok':
    'Quedan {days} días. El plan necesita unas {required} preguntas por día y vos aceptás hasta {capacity}, así que todavía nada lo vuelve imposible — que no es lo mismo que una promesa.',
  'prog.pace.date.tight':
    'Quedan {days} días. El plan necesita unas {required} preguntas por día y vos aceptás {capacity}. No cabe: movés la fecha, destildás materias, o aceptás más preguntas por día.',
  'prog.load.title': 'Hoy',
  'prog.load':
    'Hasta {limit} preguntas hoy. {sent} hechas, {answered} respondidas, y {due} listas para preguntarte.',
  'prog.load.noLimit':
    'Todavía no guardaste un horario, así que no hay un tope diario tuyo contra el cual reportar.',
  'prog.load.ceiling':
    'nabu pregunta de una en una y espera tu respuesta, así que {limit} por día es un techo y no un pronóstico. La mayoría de los días van a ser menos. Las lecciones cortas que te enseñan un tema, y cualquier recordatorio de una pregunta que sigue esperando, no cuentan.',
  'prog.load.waiting': 'Hay una pregunta pendiente. No sale nada nuevo hasta que la respondás.',
  'prog.load.paused': 'El envío está detenido, así que no está saliendo nada.',
  'prog.load.pausedBySilence':
    'El envío se detuvo solo después de tres recordatorios sin respuesta. Volvé a activarlo abajo y todo sigue donde quedó.',
  'prog.load.noZone':
    'No sabemos en qué zona horaria estás, así que nabu no puede decir qué día es donde vos estás — la cuenta de arriba puede ir contra el día equivocado.',
  'prog.subjects.title': 'Materia por materia',
  'prog.subjects.byPlan': 'Verlas en el orden del plan',
  'prog.subjects.byWeakest': 'Ver primero las más flojas',
  'prog.subject.counts':
    '{holding} de {questions} sostenidas · {struggling} por trabajar · {untested} sin preguntar',
  'prog.subject.none': 'Todavía sin preguntas',
  'prog.note.explanations':
    '{count} de los mensajes de este plan son explicaciones y no preguntas. Nadie responde esas, así que quedan fuera de todos los números de arriba.',
  'prog.note.noQuestions':
    '{count} materias que estás estudiando no tienen preguntas escritas. Quedan fuera de la cifra de arriba en vez de contar como cero.',
  'prog.note.unplaced':
    '{count} preguntas son de temas que ya no están en el plan. Se siguen preguntando, y no se cuentan en ninguna materia.',
  'prog.note.off':
    'Una materia que destildaste después de empezar todavía tiene sus preguntas en la rotación — destildar evita que se escriban nuevas, no que salgan las que ya estaban programadas.',
  'prog.note.partial':
    'Acá hay más preguntas de las que esta pantalla lee de una vez, así que cada número de arriba es un piso y no un total.',

  'sheet.title': 'Lo que has aprendido',
  'sheet.hint':
    'Una línea por cada cosa que ya te preguntaron y respondiste — no todo el plan, y nada que todavía no te hayan enseñado. Hecho para imprimir.',
  'sheet.download': 'Descargarlo en PDF',
  'sheet.working': 'Escribiéndolo…',
  'sheet.error.empty':
    'Todavía no respondiste nada, así que no hay nada que poner en la hoja. Se va llenando a medida que respondés preguntas.',
  'sheet.error.rewrite':
    'Esta vez no se pudo escribir la hoja. No se perdió nada — probá de nuevo en un momento y sigue donde quedó.',
  'sheet.error.generic': 'No se pudo descargar la hoja. Probá de nuevo.',

  'win.title': 'Cuándo te puede escribir nabu',
  'win.hint':
    'Esto queda fijo, no es parte de ningún objetivo — se mantiene como lo dejés cuando cerrás uno y arrancás otro.',
  'win.loading': 'Cargando…',
  'win.defaults': 'Todavía no guardaste nada, así que esto es lo que viene por defecto.',
  'win.limit.label': 'Cuántas preguntas por día, como máximo',
  'win.limit.unit': 'preguntas por día',
  'win.hours.label': 'Las horas en que estás disponible',
  'win.hours.from': 'Desde',
  'win.hours.until': 'Hasta',
  'win.hours.hint':
    'Fuera de esas horas no se manda nada — espera. Las dos horas se leen en tu propio reloj.',
  'win.weighting.label': 'Cómo se reparte ese número entre tus planes',
  'win.weighting.even': 'En partes iguales',
  'win.weighting.even.hint':
    'Cada plan recibe la misma parte de tus preguntas. Se pregunta del que hace más rato que no se toca.',
  'win.weighting.weighted': 'Primero los que tienen fecha',
  'win.weighting.weighted.hint':
    'Un plan recibe una parte más grande cuanto más le falta por cubrir antes de su fecha — así, un examen cargado en dos semanas se pregunta más seguido que un plan sin fecha. Nunca se abandona ninguno: todo plan con trabajo pendiente sigue apareciendo.',
  'win.weighting.onePlan':
    'Tenés un solo plan andando, así que todavía no hay nada que repartir. Esto empieza a importar cuando tenés dos.',
  'win.weighting.noDeadlines':
    'Ninguno de tus planes tiene fecha, así que "primero los que tienen fecha" queda igual que "en partes iguales" — no hay con qué estar atrasado. Ponele fecha a un plan y esto empieza a hacer algo.',
  'win.weighting.inForce':
    'Al menos uno de tus planes tiene fecha, así que este ajuste está decidiendo de cuál vas a saber más seguido.',
  'win.explanations.label': 'Mandar explicaciones, no solo preguntas',
  'win.explanations.hint':
    'Apagado, nabu solo te pregunta cosas. Encendido, te enseña el tema en unas pocas cientos de palabras antes de preguntarte, y te lo vuelve a enseñar si empezás a fallar. Eso no te gasta preguntas del día. En los dos casos, si fallás algo se te corrige igual.',
  'win.plain': '{count} preguntas por día entre las {from} y las {until} — más o menos una cada {gap}.',
  'win.plain.one': 'Una pregunta por día, en algún momento entre las {from} y las {until}.',
  'win.plain.quiet': 'Nada del todo entre las {until} y las {from}.',
  'win.plain.explanations.on':
    'Las lecciones cortas llegan aparte, las que haga falta — son las que te enseñan un tema antes de preguntarte, así que no te gastan preguntas.',
  'win.plain.explanations.off': 'Solo preguntas. No se manda nada para leer antes.',
  'win.unit.hours': 'h',
  'win.unit.minutes': 'min',
  'win.zone': 'Se leen en el reloj de donde estás:',
  'win.zone.missing':
    'No sabemos en qué zona horaria estás, así que estas horas todavía no se pueden respetar — horas sin zona horaria no son un horario. Volvé a entrar desde el teléfono que usás y se toma sola.',
  'win.save': 'Guardar esto',
  'win.saved': 'Guardado.',
  'win.working': 'Guardando…',
  'win.error.load': 'No se pudo leer tu horario actual. Recargá la página.',
  'win.error.limit': 'Entre 1 y 24 preguntas por día. Cero no es un horario — mejor cerrá el objetivo.',
  'win.error.limitWhole': 'Un número entero de preguntas por día.',
  'win.error.time': 'Esas horas no se pudieron leer. Usá los dos selectores de hora.',
  'win.error.empty': 'La hora de inicio y la de fin son la misma, así que no queda ninguna hora para escribirte.',
  'win.error.wrap':
    'Las horas tienen que empezar y terminar el mismo día. Si querés que te escriba de noche, terminá a las 23:59 y no pasada la medianoche.',
  'win.error.weighting':
    'Esa forma de repartir tus preguntas no es una que esta versión conozca. Elegí una de las dos de arriba y guardá de nuevo.',
  'win.error.generic': 'No se guardó. Probá de nuevo.',

  'chat.title': 'La conversación',
  'chat.loading': 'Leyendo la conversación…',
  'chat.empty':
    'Todavía no hay nada. La primera pregunta llega cuando haya un objetivo corriendo y estés dentro de las horas que elegiste.',
  'chat.older': 'Ver mensajes anteriores',
  'chat.input.label': 'Tu respuesta',
  'chat.input.placeholder': 'Respondé la pregunta, o pedí algo.',
  'chat.input.hint': 'Enter envía. Shift y Enter hacen un salto de línea.',
  'chat.send': 'Enviar',
  'chat.sending': 'Enviando…',
  'chat.retry': 'Enviar de nuevo',
  'chat.said.you': 'Dijiste:',
  'chat.said.nabu': 'nabu dijo:',
  'chat.error.load': 'No se pudo leer la conversación. Recargá la página.',
  'chat.error.empty': 'No hay nada que enviar.',
  'chat.error.long':
    'Es más largo de lo que la conversación puede guardar, así que quedaría guardado distinto de como se leyó. Acortalo y envialo de nuevo.',
  'chat.error.session': 'Esta sesión ya no es válida. Entrá de nuevo.',
  'chat.error.graded':
    'Tu respuesta sí se calificó, pero la contestación no llegó de vuelta. Enviarla otra vez no ayuda: la pregunta ya se cerró y la siguiente llega igual.',
  'chat.error.send': 'No se envió. Probá de nuevo.',
  'chat.error.no_objective':
    'Todavía no hay un plan del que hablar. Contale a nabu qué querés aprender y de ahí salen las preguntas.',
  'chat.error.offline': 'Sin conexión. Probá cuando tengas.',

  // See the note beside the English block: `chat.word.*` is text the system parses, not a
  // label. `más detalles`, `reclamo` and `5 más preguntas` are `MORE_FORM`, `DISPUTE_WORD`
  // and the form in `moreQuestions.ts`, and a chip that fills the box with anything else is
  // graded as an answer instead of being heard. Lower case and unquoted for the reasons that
  // note gives, and the accents stay: `plain` folds them, and a chip that filled the box
  // with `mas detalles` would be teaching a Spanish speaker to misspell their own language.
  'chat.close': 'Cerrar',
  'chat.day.today': 'Hoy',
  'chat.day.yesterday': 'Ayer',
  'chat.older.loading': 'Leyendo mensajes anteriores…',
  'chat.older.end': 'Acá empieza todo.',
  'chat.older.again': 'No se pudo cargar. Tocá para reintentar.',
  'chat.new.one': '1 mensaje nuevo',
  'chat.new.many': '{n} mensajes nuevos',

  'chat.kind.said': 'Vos',
  'chat.kind.briefing': 'Lección',
  'chat.kind.question': 'Pregunta',
  'chat.kind.reminder': 'Recordatorio',
  'chat.kind.mark': 'Calificado',
  'chat.kind.reply': 'nabu',
  'chat.kind.summary': 'Tu día',
  'chat.kind.notice': 'Aviso',
  'chat.mark.wrong': 'Incorrecto',
  'chat.mark.partial': 'A medias',
  'chat.mark.correct': 'Correcto',
  'chat.mark.effortless': 'Correcto, sin dudar',

  'chat.head.loading': 'Leyendo…',
  'chat.head.untitled': 'Tu objetivo',
  'chat.head.asked': 'Lo que pediste',
  'chat.head.noObjective': 'Todavía no hay nada corriendo. Planteá un objetivo y empiezan las preguntas.',
  'chat.head.byDate': 'Lo necesitás para el',
  'chat.head.toHold': 'Querés mantener',
  'chat.head.tapProgress': 'Ver cómo vas',
  'chat.head.progress': 'Cómo vas',

  'chat.prog.mastered': 'dominado, en todo el objetivo',
  'chat.prog.holding': 'firmes',
  'chat.prog.struggling': 'flojas',
  'chat.prog.untested': 'sin preguntar',
  'chat.prog.due': 'para hoy',
  'chat.prog.today': 'respondidas hoy',
  'chat.prog.of': 'de {n} preguntas.',
  'chat.prog.none': 'Todavía no hay nada que medir. El progreso empieza cuando haya un objetivo corriendo y hayas respondido algo.',
  'chat.prog.nothing': 'Todavía no respondiste nada, así que no hay nada que promediar.',
  'chat.prog.paused': 'Las preguntas están pausadas. Mandá “seguir” en el chat para arrancar de nuevo.',
  'chat.prog.partial': 'Tu currículo es lo bastante grande como para que esta lectura se cortara antes de terminar, así que estos números son un piso y no un total.',

  'chat.subject.title': 'De dónde salió esto',
  'chat.subject.untitled': 'Tema sin título',
  'chat.subject.unmeasured': 'Todavía no respondiste nada de este tema.',
  'chat.subject.inactive': 'Destildaste este tema en el plan. Las preguntas ya programadas siguen llegando.',
  'chat.subject.gone': 'Esto viene de un objetivo que ya no estás estudiando, así que no hay progreso actual para mostrar.',

  'chat.src.title': 'Fuentes',
  'chat.src.chip': 'De dónde salió esto',
  'chat.src.loading': 'Leyendo las fuentes…',
  'chat.src.failed': 'Las fuentes no cargaron. Tocá para reintentar.',
  'chat.src.none': 'No quedó registrado de dónde salió esto. Lo que se enseñó antes de que nabu guardara las fuentes no dice nada, y eso es lo que significa.',
  'chat.src.untitled': 'Documento sin título',
  'chat.src.kind.upload': 'Material tuyo',
  'chat.src.kind.link': 'Una página que agregaste',
  'chat.src.kind.research': 'Investigado para vos',
  'chat.src.unreadable': 'Esta no se pudo leer, así que la explicación no se apoya en ella.',
  'chat.src.trimmed': 'Cita {n} fuentes en total.',

  'chat.chips.label': 'Cosas que podés decir',
  'chat.chip.dontKnow': 'No sé',
  'chat.chip.dispute': 'Esa calificación está mal',
  'chat.chip.more': 'Contame más',
  'chat.chip.moreQuestions': 'Preguntame más',
  'chat.word.dontKnow': 'No sé',
  'chat.word.dispute': 'reclamo ',
  'chat.word.more': 'más detalles',
  'chat.word.moreQuestions': '5 más preguntas',
  'push.title': 'Notificaciones en este dispositivo',
  'push.hint':
    'Así te avisa nabu que hay una pregunta esperando. Actívalas una vez en cada teléfono, tableta o computadora donde quieras que te pregunte: es una preferencia de este dispositivo, no de tu cuenta.',
  'push.checking': 'Revisando…',
  'push.on': 'Activadas en este dispositivo.',
  'push.off': 'Desactivadas en este dispositivo.',
  'push.enable': 'Activar notificaciones',
  'push.disable': 'Desactivarlas aquí',
  'push.working': 'Un momento…',
  'push.devices.none': 'Todavía no hay ningún dispositivo registrado.',
  'push.devices.one': 'Un dispositivo registrado.',
  'push.devices.many': '{count} dispositivos registrados.',
  'push.stale':
    'Este dispositivo cree que las notificaciones están activas, pero nabu no tiene registro de él. Desactívalas y vuelve a activarlas aquí.',
  'install.title': 'Poné nabu en tu pantalla de inicio',
  'install.why': 'Se abre como una app, sin la barra del navegador en medio.',
  'install.why.ios':
    'En iPhone y iPad es la única forma en que podemos avisarte cuando toca una pregunta.',
  'install.add': 'Agregar a la pantalla de inicio',
  'install.ios.share': 'Tocá el botón Compartir',
  'install.ios.add': 'Elegí “Agregar a inicio”',
  'install.ios.open': 'Abrir en Safari para instalar',
  'install.ios.manual': 'Tu navegador no pudo pasar el enlace. Abrí nabu.guru en Safari y después:',
  'install.copy': 'Copiar la dirección',
  'install.copied': 'Copiado',
  'push.install.title': 'Primero agrega nabu a tu pantalla de inicio',
  'push.install.body':
    'En iPhone y iPad las notificaciones solo funcionan cuando la app está en la pantalla de inicio. Tocá Compartir, luego “Agregar a inicio”, abrí nabu desde ahí y volvé a esta pantalla.',
  'push.unsupported':
    'Este navegador no puede mostrar notificaciones. Chrome, Safari 16.4 o posterior y Firefox sí pueden.',
  'push.denied':
    'Este navegador está bloqueando las notificaciones de nabu. Desde aquí no se puede deshacer: hay que cambiarlo en la configuración del navegador para este sitio y después podés activarlas de nuevo.',
  'push.error.notConfigured':
    'Las notificaciones todavía no están configuradas en este servicio. No es un problema de tu dispositivo.',
  'push.error.generic': 'No se pudo. Probá de nuevo.',
  'push.test': 'Enviarme una prueba',
  'push.test.hint':
    'Sale igual que una pregunta de verdad, así que si llega acá es porque nabu puede escribirte. De eso se trata.',
  'push.test.sent': 'Va en camino. Si no llega nada en un minuto, las notificaciones no te están llegando.',
  'push.test.working': 'Enviando…',
  'push.test.error': 'No se pudo enviar la prueba. Probá de nuevo en un momento.',
  'push.test.unreachable':
    'Todavía no hay a dónde mandarla. Activá las notificaciones arriba: nabu no tiene otra forma de escribirle a esta cuenta.',

  // ---- La navegación: cinco secciones, y la conversación por encima de ellas ----
  'nav.sections': 'Secciones',
  'nav.chat': 'Chat',
  'nav.progress': 'Tu avance',
  'nav.plan': 'Mi plan',
  'tabs.label': 'Tus planes',
  'tabs.plan': 'Plan',
  'tabs.unread': 'sin leer',
  'nav.data': 'Investigación y datos',
  'nav.config': 'Configuración',
  'nav.profile': 'Perfil',

  'plan.objective.title': 'Tu objetivo',
  'plan.step.title': 'Qué sigue',
  'plan.close.title': 'Terminar con este objetivo',
  'plan.none.title': 'Todavía no hay objetivo',
  'plan.none.body':
    'Esta sección trabaja sobre lo que estés estudiando, y todavía no dijiste qué es. Decilo en tu plan y todo esto se llena.',
  'plan.none.action': 'Ir a mi plan',

  'cfg.target.title': 'Tu meta',

  'profile.account.title': 'Tu cuenta',
  'profile.account.body':
    'Entrás con un código de seis dígitos que te llega al correo, así que no hay contraseña que recordar. Las preguntas llegan al chat, y las notificaciones son las que te avisan que hay una esperando.',

  'prog.idle':
    'Todavía no hay nada que medir. Esto se llena cuando hay un objetivo corriendo y ya respondiste las primeras preguntas.',

  'welcome.title': 'Una cosa antes',
  'welcome.lede':
    'nabu está investigando tu objetivo en este momento. Eso toma de cinco a quince minutos y no hay nada que mirar, así que usemos ese rato en lo único de lo que depende todo lo demás.',
  'welcome.why':
    'Hasta que digas cuándo puede buscarte nabu, no se te puede entregar nada de lo que arme: ni resúmenes, ni preguntas, ni notificaciones. Es la única configuración sin la cual el producto no funciona, y por eso se pide acá y no en una pantalla que no tendrías por qué abrir.',
  'welcome.deciding': 'Un momento…',
  'welcome.done.title': 'Ya se te puede buscar',
  'welcome.done.body':
    'Esa era la parte que había que decidir. La investigación sigue corriendo y tu plan va a estar esperándote cuando termine. Antes de eso, mirá lo que te va a llegar.',
  'welcome.done.go': 'Mostrame qué llega',

  'tour.title': 'Qué te va a llegar',
  'tour.lede':
    'Dos ejemplos, para que sepas qué estás viendo cuando lleguen los de verdad. Nada de esto se te envió ni está en tu historial: tu primer resumen todavía se está escribiendo.',
  'tour.tag': 'Ejemplo',
  'tour.sample.briefing':
    '📖 {subject}\n\nEsta es la forma en que llega un resumen: unos cientos de palabras sobre un solo tema, escritas a partir de lo que nabu efectivamente leyó — el dato, la razón detrás y la parte que a la gente se le suele escapar. Lo leés. No se espera nada de vuelta.',
  'tour.sample.question':
    '❓ {second}: decí lo único que te gustaría poder recordar de memoria sobre esto.\n\nEsta es la forma en que llega una pregunta. Una de verdad se responde con tus palabras, en el cuadro de abajo del chat, y lo que se contesta decide cuándo vuelve a aparecer: antes si costó, después si no.',
  'tour.note':
    'Estos dos son ilustraciones y no se pueden responder: no hay nada detrás que calificar. No hay nada esperándote: la primera pregunta de verdad llega sola, con una notificación, cuando tu plan esté armado.',
  'tour.subject.fallback': 'lo que estás aprendiendo',
  'tour.done': 'Entendido',

  'landing.deciding': 'Un momento…',
  'landing.failed': 'nabu no logró comunicarse con su propio servidor en este momento.',
  'landing.retry': 'Reintentar',
  'update.ready': 'Hay una versión nueva de nabu.',
  'update.reload': 'Actualizar',
  'footer.built': 'Hecho en Costa Rica.',
};

const DICTIONARIES: Record<Locale, Record<CopyKey, string>> = { en, es };

export const LOCALE_STORAGE_KEY = 'nabu.locale';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * The locale to start in.
 *
 * A remembered choice always wins — somebody who switched to English meant it.
 * Otherwise the browser's preference, and Spanish if it says nothing useful.
 * Wrapped because a browser with site data blocked throws on the read rather
 * than returning null, and a landing page must not fail to render over that.
 */
export function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Storage unavailable. Fall through to the browser's preference.
  }
  const preferred = typeof navigator === 'undefined' ? '' : navigator.language.slice(0, 2);
  return isLocale(preferred) ? preferred : DEFAULT_LOCALE;
}

export interface I18n {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: (key: CopyKey) => string;
}

export const I18nContext = createContext<I18n | null>(null);

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n used outside the provider');
  return value;
}

/**
 * A localised map resolved for the reader, then for anybody.
 *
 * Falls back to whatever language the text was generated in before falling back
 * to the placeholder: a topic titled in Spanish on an English screen is still
 * the name of that topic, and far more use than "Topic without a title".
 *
 * It lives here rather than beside either of its callers because objective
 * titles and curriculum nodes were on course to grow two fallbacks that
 * disagreed — one screen showing the Spanish title and the next showing the
 * placeholder for the same objective.
 */
export function localised(copy: Localised, locale: Locale, fallback: string): string {
  return copy[locale] ?? Object.values(copy)[0] ?? fallback;
}

export function translate(locale: Locale, key: CopyKey): string {
  return DICTIONARIES[locale][key];
}
